import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import lodashClonedeep from "lodash.clonedeep";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { EHttpMethod } from "@/services/api-service/enums";
import { useGomakeAxios, useGomakeRouter, useSnackBar } from "@/hooks";
import { useUserPermission } from "@/hooks/use-permission";
import { Permissions } from "@/components/CheckPermission/enum";
import { permissionsState } from "@/store/permissions";
import { getAndSetAllEmployees } from "@/services/hooks";
import { v4 as uuidv4 } from "uuid";
import {
  getExchangeRateFromCurrency,
  isSameCurrency,
} from "@/utils/document-currency";
import {
  IContactData,
  agentListsState,
  clientContactsState,
  loadingWithShadowState,
  quoteConfirmationState,
  quoteItemState,
  openReceiptCreationModalState,
  systemVATState,
  systemCurrencyState,
  selectedFirstMaterialState,
} from "@/store";
import { useQuoteGetData } from "./use-quote-get-data";
import { useNotifyPackagingOutdated } from "@/pages-components/packaging/use-notify-packaging-outdated";
import { notifyDocumentShippingInvalidated } from "@/pages-components/packaging/shipping-invalidation";
import { shouldWarnPackagingOnItemDelete } from "@/pages-components/packaging/utils";
import { directMessagesService } from "@/services/frontdesk/api/direct-messages.service";
import {
  buildQuoteApprovalResultMessage,
  extractQuoteApprovalNumberFromTitle,
} from "@/widgets/quote-new/buttons-container/build-quote-approval-message";
import { applyClientAgentToDocument } from "@/widgets/quote-new/utils/default-document-agent";
import {
  internalChatPopupConversationIdAtom,
  internalChatPopupMinimizedAtom,
  internalChatPopupOpenAtom,
} from "@/store/frontdesk/atoms/internal-chat-popup.atoms";
import { buildRoutePath, RouteBuilder } from "@/widgets/frontdesk/routing";
import {
  acceptDocumentApi,
  addDeliveryApi,
  addDocumentContactApi,
  calculateDocumentApi,
  calculateDocumentItemApi,
  cancelDocumentApi,
  changeDocumentClientApi,
  createDocumentApi,
  createNewDocumentApi,
  deleteDeliveryItemApi,
  deleteDocumentAddressApi,
  deleteDocumentContactApi,
  deleteDocumentItemApi,
  duplicateWithAnotherQuantityApi,
  getAllClientActiveBudgetsApi,
  getDocumentApi,
  DocumentDeliveryDateData,
  getDocumentDeliveryDateApi,
  getWhatsAppMessageApi,
  getWhatsAppMessageForReceiptApi,
  moveOrderToReadyApi,
  reCalculateItemApi,
  refreshExchangeRateApi,
  rejectDocumentApi,
  removeDraftDocumentApi,
  saveDocumentApi,
  saveDraftDocumentApi,
  sendDocumentToClientApi,
  sendReceiptToClientApi,
  sortDocumentItemsApi,
  updateAgentApi,
  updateCustomerSupportRepresentativeApi,
  updatePaymentRequiredApi,
  updateDiscountTypeApi,
  updateDocuementItemSelectApi,
  updateDocumentApi,
  updateDocumentContactApi,
  updateDocumentCurrencyApi,
  updateDocumentPreferenceApi,
  updateHideDiscountColumnApi,
  updateDocumentTitleApi,
  updateDocumentStatusApi,
  updateDocumentVatStatusApi,
  updateDocumentDefaultLanguageApi,
  updateDueDateApi,
  updateExchangeRateApi,
  updateOccasionalClientNameApi,
  updatePurchaseNumberApi,
  checkPurchaseNumberExistsApi,
  updateQuoteItemQuantityApi,
  updateQuoteClientNoteAsShownApi,
  getDocumentInventoryApi,
  getPurchaseOrderIntakeHistoryApi,
  getDocumentItemRelatedDocumentsApi,
  undoDocumentIntakeApi,
  undoDocumentItemIntakeApi,
} from "@/services/api-service/generic-doc/documents-api";
import {
  displayedDeliveryDate,
  formatDeliveryDateForSave,
  retainDeliveryDatePair,
  type DeliveryDateSource,
} from "./delivery-date";
import { DOCUMENT_TYPE, StatusTitleText } from "../quotes/enums";
import { useRouter } from "next/router";
import {
  getAllCreditTransactionsApi,
  getClientPaymentItemsApi,
} from "@/services/api-service/generic-doc/receipts-api";
import {
  checkedItemsIdsState,
  creditTransactionsState,
  transactionOptionsData,
  checksRowState,
  CheckData,
} from "@/widgets/quote-new/buttons-container/states";
import { QuoteStatuses } from "@/widgets/quote-new/total-price-and-vat/enums";
import {
  isSelectedCLoseAsDeliveryNoteState,
  isSelectedCLoseAsProformaInvoiceState,
} from "@/pages/customers/customer-states";
import {
  checkArrayNotEmptyOrEmailNotEmpty,
  checkArrayNotEmptyOrPhoneNotEmpty,
  isAtLeastOneSelected,
} from "@/utils/helpers";
import { useWhatsAppWeb } from "@/pages-components/quote-new/use-whats-app-web";
import { EAddressType } from "@/widgets/quote-new/business-widget/address-widget/state";
import { ClientChangeSource, hasTableCellUpdatesState } from "./store/quote";
import { normalizeDocumentItemsDetailsByDefaultLanguage } from "@/widgets/quote-new/utils/document-item-translations";
import {
  toPersistedSortLayers,
  type QuoteItemSortRule,
} from "@/widgets/quote-new/quote-table/items-sort/sort-quote-items";
import { EnumVATStatus } from "@/enums/accounting-enums";
import { useClientTypesList } from "@/hooks/use-client-types";
import { useModal } from "@/hooks/use-modal";
import {
  printHouseHasFrontDeskFeature,
  printHouseProfile,
} from "@/store/print-house-profile";
import { EPreferences } from "@/enums/prefernces";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { useDocumentTitle } from "@/pages-components/quote-new/use-document-title";
import cloneDeep from "lodash.clonedeep";
import { Button } from "@mui/material";
import { PlusIcon } from "../quotes/widgets/icons/header-section-icons";
import { useStyle } from "./style";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useTheme } from "@/contexts/theme";
import { useFrontDeskEmailIntegration } from "../quotes/hooks/use-frontdesk-email-integration";
import { useStartOptimisticQuickBooksTax } from "@/hooks/use-start-optimistic-quickbooks-tax";
import {
  buildAllDocumentItemsForItemCalculation,
  buildDraftCalculateDocumentPayload,
  isDraftInvoiceQuickBooksTax,
  mergeDraftTaxCalculationResult,
  mergeDraftTaxDocumentCalculationResult,
} from "@/widgets/quote-new/utils/draft-invoice-tax";
import { shouldShowSystemVatMismatchWarning } from "@/widgets/quote-new/utils/quickbooks-tax-ui";
import {
  FinalPriceUpdateChoice,
  resolveFinalPriceUpdateChoice,
} from "@/widgets/quote-new/utils/final-price-update-choice";
import {
  StatePayment,
  sendPaymentState,
} from "@/pages-components/quotes/widgets/send-payment-modal/states";
import { getDocumentContactApi } from "@/services/api-service/pay/pay-api";
import {
  PurchaseOrderType,
  ShowDocumentDetailsEnum,
  ShowPricesEnum,
  ItemCalculationType,
  PurchaseOrderStatuses,
  DocumentCalculationType,
} from "@/enums";
import { assignToTicketApi } from "@/services/api-service/generic-doc/documents-api";
import { isErpTicketDocumentType } from "@/widgets/frontdesk/providers/chatwoot/utils/document/related-documents.utils";
import { isPurchaseOrderStatusChangeLocked } from "@/utils/purchase-order-utils";
import { useFrontDeskDocumentSend } from "./use-frontdesk-document-send";

export enum MessageType {
  Email = 0,
  SMS = 1,
  Both = 2,
  FrontDesk = 3,
}

const DOCUMENT_TYPES_WITH_IDEMPOTENCY_KEY = [
  DOCUMENT_TYPE.deliveryNote,
  DOCUMENT_TYPE.deliveryNoteRefund,
  DOCUMENT_TYPE.proformaInvoice,
  DOCUMENT_TYPE.invoice,
  DOCUMENT_TYPE.invoiceRefund,
  DOCUMENT_TYPE.receipt,
];

const resolveItemPrevFinalPrice = (
  quoteItem: any,
  quoteItemEdit: any,
  tableItem: any,
  calculationType: number,
): number => {
  const totalFromView =
    tableItem?.finalPrice ??
    quoteItemEdit?.finalPrice ??
    quoteItem?.finalPrice;

  if (calculationType === ItemCalculationType.finalPrice) {
    const prevBeforeTotalEdit =
      tableItem?.prevFinalPrice ??
      quoteItemEdit?.prevFinalPrice ??
      quoteItem?.prevFinalPrice;

    if (
      prevBeforeTotalEdit != null &&
      Number(prevBeforeTotalEdit) !== Number(totalFromView)
    ) {
      return prevBeforeTotalEdit;
    }
  }

  return totalFromView ?? 0;
};

interface IQuoteProps {
  docType: DOCUMENT_TYPE;
  isQuoteConfirmation?: boolean;
  isPrePaymentPage?: boolean;
}
const useQuoteNew = ({
  docType,
  isQuoteConfirmation = false,
  isPrePaymentPage = false,
}: IQuoteProps) => {
  const documentPath = DOCUMENT_TYPE[docType];
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const RTL = dir === "rtl";
  const { classes } = useStyle(false);

  const {
    alertSuccessUpdate,
    alertFaultUpdate,
    alertSuccessAdded,
    alertFaultAdded,
    alertSuccessDelete,
    alertFaultDelete,
    alertFault,
    alertFaultRaw,
    alertSuccess,
    alertSuccessSend,
    alertFaultSend,
    alertWarning,
  } = useSnackBar();
  const systemVAT = useRecoilValue<number>(systemVATState);
  const systemCurrency = useRecoilValue<string>(systemCurrencyState);
  const AddCustomerModal = useModal();
  const printHouseProfileState = useRecoilValue<any>(printHouseProfile);

  const router = useRouter();
  const { callApi } = useGomakeAxios();
  const notifyPackagingOutdated = useNotifyPackagingOutdated();
  const { primaryColor, successColor } = useGomakeTheme();
  const { currentTheme } = useTheme();
  const isHp = currentTheme === "hp-client";
  const { openWhatsAppWebLink } = useWhatsAppWeb();
  const { navigate } = useGomakeRouter();
  const setInternalChatOpen = useSetRecoilState(internalChatPopupOpenAtom);
  const setInternalChatMinimized = useSetRecoilState(
    internalChatPopupMinimizedAtom,
  );
  const setInternalChatConversationId = useSetRecoilState(
    internalChatPopupConversationIdAtom,
  );

  // Alert VAT Mismatch Modal state
  const [openVatMismatchModal, setOpenVatMismatchModal] = useState(false);
  const [openTaxConfirmationWarningModal, setOpenTaxConfirmationWarningModal] =
    useState(false);

  const { getQuote, getAllClientContacts } = useQuoteGetData(
    docType,
    systemVAT,
    setOpenVatMismatchModal,
    true,
  );
  const [quoteItemValue, setQuoteItemValue] =
    useRecoilState<any>(quoteItemState);
  const [quoteConfirm, setQuoteConfirm] = useRecoilState<any>(
    quoteConfirmationState,
  );
  const [openReceiptCreationModal, setOpenReceiptCreationModal] =
    useRecoilState(openReceiptCreationModalState);
  const checksReceipt = useRecoilValue<CheckData[]>(checksRowState);

  const isOutgoingPayments = router.query.isOutgoingPayments === "true";
  const isSupplierPayments = docType === DOCUMENT_TYPE.supplierPayments;
  const isPurchaseOrder = docType === DOCUMENT_TYPE.purchaseOrder;
  const isPurchaseInvoice = docType === DOCUMENT_TYPE.purchaseInvoice;
  const isPurchaseInvoiceRefund =
    docType === DOCUMENT_TYPE.purchaseInvoiceRefund;

  const isPurchaseDocument =
    isPurchaseOrder ||
    isPurchaseInvoice ||
    isPurchaseInvoiceRefund ||
    isSupplierPayments ||
    isOutgoingPayments;

  // FrontDesk Email Integration
  const {
    isEmailComposerOpen,
    emailData,
    openEmailComposer,
    closeEmailComposer,
    isLoadingEmailData,
  } = useFrontDeskEmailIntegration({
    documentType: docType,
  });

  // Wrap closeEmailComposer to reset order update flag
  const handleCloseEmailComposer = useCallback(() => {
    closeEmailComposer();
    setIsOrderUpdateEmailComposer(false);
  }, [closeEmailComposer]);

  const purchaseOrderStatusOptions = useMemo(
    () =>
      Object.keys(PurchaseOrderStatuses)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
          value:
            PurchaseOrderStatuses[key as keyof typeof PurchaseOrderStatuses],
          label: t(`documentStatus.PurchaseOrder.${key}`),
        })),
    [t],
  );
  const [isUpdatingPurchaseStatus, setIsUpdatingPurchaseStatus] =
    useState(false);

  const [creationDate, setCreationDate] = useState(
    isQuoteConfirmation
      ? quoteConfirm?.creationDate
      : quoteItemValue?.creationDate,
  );
  const [taxDate, setTaxDate] = useState(
    isQuoteConfirmation ? quoteConfirm?.taxDate : quoteItemValue?.taxDate,
  );

  const [selectDate, setSelectDate] = useState<string | null>(null); // Represents the Delivery Date for Quotes and Orders, and the Due Date for other documents
  const deliveryDateSourceRef = useRef<DeliveryDateSource>("user");
  const deliveryDatesRef = useRef<{
    systemDueDate: string | null;
    userDueDate: string | null;
  }>({ systemDueDate: null, userDueDate: null });
  const dateBeforeEditRef = useRef<string | null>(null);
  const userDueBeforeEditRef = useRef<string | null>(null);
  const systemDueBeforeEditRef = useRef<string | null>(null);
  const sourceBeforeEditRef = useRef<DeliveryDateSource>("user");

  // const [selectDate, setSelectDate] = useState(
  //   isQuoteConfirmation ? quoteConfirm?.dueDate : quoteItemValue?.dueDate
  // );

  const [isUpdateBusinessName, setIsUpdateBusinessName] = useState<
    number | null
  >(null);
  const [isUpdatePurchaseNumber, setIsUpdatePurchaseNumber] = useState<
    number | null
  >(null);
  const [isUpdateDocumentTitle, setIsUpdateDocumentTitle] = useState<
    number | null
  >(null);
  const [isUpdateClientName, setIsUpdateClientName] = useState<number | null>(
    null,
  );
  const [clientName, setClientName] = useState(
    quoteItemValue?.occasionalClientName,
  );

  const [isUpdateExchangeRate, setIsUpdateExchangeRate] = useState<
    number | null
  >(null);

  const [isUpdateCurrency, setIsUpdateCurrency] = useState<string>(null);
  const [isUpdateVatStatus, setIsUpdateVatStatus] = useState<number | null>(
    null,
  );
  const [isUpdateDefaultLanguage, setIsUpdateDefaultLanguage] = useState<
    number | null
  >(null);
  const [, setIsUpdateBusinessCode] = useState<number | null>(null);
  const [isUpdateAddress, setIsUpdateAddress] = useState<number | null>(null);
  const [isUpdateAgent, setIsUpdateAgent] = useState<number | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [agentListValue, setAgentListValue] =
    useRecoilState<{ text: string; value: string }[]>(agentListsState);
  const [
    isUpdateCustomerSupportRepresentative,
    setIsUpdateCustomerSupportRepresentative,
  ] = useState<number | null>(null);
  const [
    selectedCustomerSupportRepresentative,
    setSelectedCustomerSupportRepresentative,
  ] = useState<any>();
  const [
    customerSupportRepresentativeListValue,
    setCustomerSupportRepresentativeListValue,
  ] = useState<{ text: string; value: string }[]>([]);
  const [isDisplayWidget, setIsDisplayWidget] = useState(false);
  const [items, setItems] = useState([]);
  const [reasonText, setReasonText] = useState("");
  const [isUpdateContactName, setIsUpdateContactName] = useState(null);
  const [isUpdateContactEmail, setIsUpdateContactEmail] = useState(null);
  const [isUpdateContactMobile, setIsUpdateContactMobile] = useState(null);
  const [isUpdateContactName1, setIsUpdateContactName1] = useState(null);
  const [isUpdateContactEmail1, setIsUpdateContactEmail1] = useState(null);
  const [isUpdateContactMobile1, setIsUpdateContactMobile1] = useState(null);
  const [clientContactsValue, setClientContactsValue] =
    useRecoilState<IContactData[]>(clientContactsState);
  const [selectedContact, setSelectedContact] = useState();
  const [openDeleteModalContact, setOpenDeleteModalContact] = useState(false);
  const [openAddNewItemModal, setOpenAddNewItemModal] = useState(false);
  const [openAddNewContactModal, setOpenAddNewContactModal] = useState(false);
  const [openCopyFromOrderModal, setOpenCopyFromOrderModal] = useState(false);
  const [quoteItemId, setQuateItemId] = useState();
  const [quoteItem, setQuoteItem] = useState();
  const [
    openDuplicateWithDifferentQTYModal,
    setOpenDuplicateWithDifferentQTYModal,
  ] = useState(false);
  const [isAddQuantity, setIsAddQuantity] = useState(false);

  const [openQuickDuplicateModal, setOpenQuickDuplicateModal] = useState(false);
  const [selectedContactById, setSelectedContactById] = useState<any>();
  const [amountValue, setAmountValue] = useState();
  const [openDeleteItemModal, setOpenDeleteItemModal] = useState(false);
  const [documentItems, setPriceListItems] = useState<any>([]);
  const [quoteItems, setQuoteItems] = useState<any>([]);
  const { startOptimisticTaxCalculation } = useStartOptimisticQuickBooksTax({
    isQuoteConfirmation,
    syncQuoteItems: setQuoteItems,
  });
  const [anchorElCancelBtn, setAnchorElCancelBtn] =
    useState<null | HTMLElement>(null);
  const [anchorElSendBtn, setAnchorElSendBtn] = useState<null | HTMLElement>(
    null,
  );
  const [anchorElSettingMenu, setAnchorElSettingMenu] =
    useState<null | HTMLElement>(null);
  const [anchorElSettingMenuForVat, setAnchorElSettingMenuForVat] =
    useState<null | HTMLElement>(null);
  const [displayedItems, setDisplayedItems] = useState<number>(2);
  const openSendBtn = Boolean(anchorElSendBtn);
  const openCancelBtn = Boolean(anchorElCancelBtn);
  const openSettingMenu = Boolean(anchorElSettingMenu);
  const openNewSttingMenuForVat = Boolean(anchorElSettingMenuForVat);
  const isQuote = docType === DOCUMENT_TYPE.quote;
  const isOrder = docType === DOCUMENT_TYPE.order;
  const isDeliveryNote =
    docType === DOCUMENT_TYPE.deliveryNote ||
    docType === DOCUMENT_TYPE.deliveryNoteRefund;
  const isInvoice =
    docType === DOCUMENT_TYPE.invoice ||
    docType === DOCUMENT_TYPE.invoiceRefund;
  const isReceipt = docType === DOCUMENT_TYPE.receipt;
  const isNewCreation = router.query.isNewCreation === "true";
  const permissions = useRecoilValue<any>(permissionsState);
  const { CheckPermission } = useUserPermission();
  const permissionsReady = Array.isArray(permissions) && permissions.length > 0;
  const canCreateInvoice = CheckPermission(Permissions.CREATE_INVOICE);
  const isCreatingSalesInvoice =
    docType === DOCUMENT_TYPE.invoice && isNewCreation;
  const blockInvoiceCreation =
    isCreatingSalesInvoice && (!permissionsReady || !canCreateInvoice);
  const isDraftInvoiceQuickBooksTaxEnabled = useMemo(
    () =>
      isDraftInvoiceQuickBooksTax(
        docType,
        isNewCreation,
        printHouseProfileState?.erpType,
      ),
    [docType, isNewCreation, printHouseProfileState?.erpType],
  );
  const recalculateDraftInvoiceDocument = useCallback(
    async (quoteValue: any, overrides: Record<string, any> = {}) => {
      if (!isDraftInvoiceQuickBooksTaxEnabled) {
        return null;
      }

      startOptimisticTaxCalculation();
      const res = await callApi(
        EHttpMethod.POST,
        `/v1/erp-service/documents/calculate-document-new`,
        {
          documentType: docType,
          document: buildDraftCalculateDocumentPayload(quoteValue, overrides),
        },
        false,
      );

      if (res?.success) {
        return res?.data?.data?.data;
      }

      return null;
    },
    [
      callApi,
      docType,
      isDraftInvoiceQuickBooksTaxEnabled,
      startOptimisticTaxCalculation,
    ],
  );
  const applyDraftInvoiceDocumentState = useCallback(
    (nextValue: any) => {
      setQuoteItemValue(nextValue);
      setQuoteItems((prev) => ({
        ...prev,
        taxCalculating: nextValue?.taxCalculating ?? false,
        taxComposition: nextValue?.taxComposition ?? null,
        totalVAT: nextValue?.totalVAT,
        vat: nextValue?.vat,
        totalPayment: nextValue?.totalPayment,
        totalPrice: nextValue?.totalPrice,
        totalPriceAfterDiscount: nextValue?.totalPriceAfterDiscount,
        discount: nextValue?.discount,
        discountAmount: nextValue?.discountAmount,
      }));
    },
    [setQuoteItemValue, setQuoteItems],
  );
  const isMaterial = router.query.isMaterial === "true";
  const shouldUseDocumentIdempotencyKey =
    isNewCreation && DOCUMENT_TYPES_WITH_IDEMPOTENCY_KEY.includes(docType);
  const documentIdempotencyKeyRef = useRef<string | null>(null);
  const idempotencyKeyFromQuery = Array.isArray(router.query.idempotencyKey)
    ? router.query.idempotencyKey[0]
    : router.query.idempotencyKey;
  const getDocumentIdempotencyKey = useCallback(() => {
    if (idempotencyKeyFromQuery) {
      documentIdempotencyKeyRef.current = idempotencyKeyFromQuery;
      return idempotencyKeyFromQuery;
    }

    if (!documentIdempotencyKeyRef.current) {
      documentIdempotencyKeyRef.current = uuidv4();
    }

    return documentIdempotencyKeyRef.current;
  }, [idempotencyKeyFromQuery]);

  // After a successful create we often stay on the same page (e.g. receipt Id=… then "Create new").
  // Without clearing, documentIdempotencyKeyRef would keep the previous draft key and the next
  // create would reuse it while the backend already recorded the first request for that key.
  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (!isNewCreation) {
      documentIdempotencyKeyRef.current = null;
    }
  }, [router.isReady, isNewCreation]);

  // FrontDesk Chatwoot integration - for invoice linking
  const chatwootTicketId = router.query.ticketId as string | undefined;

  const [openWhatsAppModal, setOpenWatsAppModal] = useState(false);
  const [isSelectedAtLeastOne, setIsSelectedAtLeastOne] = useState(null);
  const [openNewItemNotesModal, setOpenNewItemNotesModal] = useState(false);
  const [openRelatedDocumentsModal, setOpenRelatedDocumentsModal] =
    useState(false);
  const [openInvoiceCreationModal, setOpenInvoiceCreationModal] =
    useState(false);

  const onClickOpenInvoiceCreation = () => {
    setOpenInvoiceCreationModal(true);
  };
  const onClickCloseInvoiceCreation = () => {
    setOpenInvoiceCreationModal(false);
  };

  useEffect(() => {
    if (
      !router.isReady ||
      blockInvoiceCreation ||
      !shouldUseDocumentIdempotencyKey ||
      idempotencyKeyFromQuery
    ) {
      return;
    }

    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          idempotencyKey: getDocumentIdempotencyKey(),
        },
      },
      undefined,
      { shallow: true },
    );
  }, [
    blockInvoiceCreation,
    getDocumentIdempotencyKey,
    idempotencyKeyFromQuery,
    router,
    shouldUseDocumentIdempotencyKey,
  ]);

  const onClickOpenRelatedDocumentsModal = () => {
    setOpenRelatedDocumentsModal(true);
  };
  const onClickCloseRelatedDocumentsModal = () => {
    setOpenRelatedDocumentsModal(false);
  };

  const onClickOpenNewItemNotesModal = () => {
    setOpenNewItemNotesModal(true);
  };
  const onClickCloseNewItemNotesModal = async () => {
    setOpenNewItemNotesModal(false);

    // Call API to update isClientNoteShown flag when closing the modal
    // Only call if flag is null or false (hasn't been set yet)
    if (
      quoteItemValue?.id &&
      docType === DOCUMENT_TYPE.quote &&
      (quoteItemValue?.client?.isClientNoteShown === null ||
        quoteItemValue?.client?.isClientNoteShown === false ||
        quoteItemValue?.isClientNoteShown === null ||
        quoteItemValue?.isClientNoteShown === false)
    ) {
      const callBack = (res) => {
        if (res?.success) {
          // Refresh the quote to get updated flag
          getQuote();
        }
      };
      await updateQuoteClientNoteAsShownApi(callApi, callBack, {
        quoteId: quoteItemValue?.id,
      });
    }
  };

  useEffect(() => {
    if (
      quoteItemValue?.client?.newItemNotes &&
      quoteItemValue?.client?.newItemNotes.trim() !== "" &&
      quoteItemValue?.client?.newItemNotes.trim() !== null &&
      docType === DOCUMENT_TYPE.quote &&
      (quoteItemValue?.client?.isClientNoteShown === null ||
        quoteItemValue?.client?.isClientNoteShown === false ||
        quoteItemValue?.isClientNoteShown === null ||
        quoteItemValue?.isClientNoteShown === false)
    ) {
      onClickOpenNewItemNotesModal();
    }
  }, [quoteItemValue]);

  useEffect(() => {
    const data = isAtLeastOneSelected(quoteItemValue?.documentItems);
    setIsSelectedAtLeastOne(data);
  }, [quoteItemValue]);

  const onClickOpenWhatsAppModal = (includeInvoices = false) => {
    setSendWithInvoices(includeInvoices);
    setOpenWatsAppModal(true);
  };
  const onClickCloseWhatsAppModal = () => {
    setSendWithInvoices(false);
    setOpenWatsAppModal(false);
  };

  const handleSettingMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElSettingMenu(event.currentTarget);
  };
  const handleSettingMenuClose = () => {
    setAnchorElSettingMenu(null);
  };
  const handleSettingMenuClickForVat = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setAnchorElSettingMenuForVat(event.currentTarget);
  };
  const handleSettingMenuCloseForVat = () => {
    setAnchorElSettingMenuForVat(null);
  };
  const handleCancelBtnClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElCancelBtn(event.currentTarget);
  };
  const handleCancelBtnClose = () => {
    setAnchorElCancelBtn(null);
  };

  const handleSendBtnClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElSendBtn(event.currentTarget);
  };

  const handleSendBtnClose = () => {
    setAnchorElSendBtn(null);
  };

  const [openAddDeliveryModal, setOpenAddDeliveryModal] = useState(false);
  const [openUpdateDeliveryModal, setOpenUpdateDeliveryModal] = useState(false);
  const [openAddAttachmentsModal, setOpenAddAttachmentsModal] = useState(false);
  const [openAddAttachmentsPurchaseModal, setOpenAddAttachmentsPurchaseModal] =
    useState(false);
  const [openDeleteDeliveryModal, setOpenDeleteDeliveryModal] = useState(false);
  const onClickOpenDeleteDeliveryModal = () => {
    setOpenDeleteDeliveryModal(true);
  };
  const onClickCloseDeleteDeliveryModal = () => {
    setOpenDeleteDeliveryModal(false);
  };
  const onOpenAddAttachmentsModal = () => {
    setOpenAddAttachmentsModal(true);
  };

  const onCloseAddAttachmentsModal = () => {
    setOpenAddAttachmentsModal(false);
  };
  const onOpenAddAttachmentsPurchaseModal = () => {
    setOpenAddAttachmentsPurchaseModal(true);
  };
  const onCloseAddAttachmentsPurchaseModal = () => {
    setOpenAddAttachmentsPurchaseModal(false);
  };
  const onOpenDeliveryModal = () => {
    setOpenAddDeliveryModal(true);
  };
  const onCloseDeliveryModal = () => {
    setOpenAddDeliveryModal(false);
  };

  const onOpenUpdateDeliveryModal = () => {
    setOpenUpdateDeliveryModal(true);
  };
  const onCloseUpdateDeliveryModal = () => {
    setOpenUpdateDeliveryModal(false);
  };

  const [isOpenRelatedDocuments, setIsOpenRelatedDocuments] = useState(true);
  const [isMoveToReady, setIsMoveToReady] = useState(false);
  const [moveToReadyOption, setMoveToReadyOption] = useState<
    "withMessage" | "withoutMessage"
  >("withoutMessage");

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMoveToReadyOption(
      event.target.value as "withMessage" | "withoutMessage",
    );
  };

  const handleCheckboxChange = () => {
    const newValue = !isMoveToReady;
    setIsMoveToReady(newValue);

    if (newValue) {
      setMoveToReadyOption("withoutMessage"); // default to "without message" when checked
    }
  };

  const [openChangeProductionStatusModal, setOpenChangeProductionStatusModal] =
    useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({});
  const isSelectedCLoseAsDeliveryNote = useRecoilValue(
    isSelectedCLoseAsDeliveryNoteState,
  );
  const isSelectedCLoseAsProformaInvoice = useRecoilValue(
    isSelectedCLoseAsProformaInvoiceState,
  );
  const onClickCloseChangeProductionStatusModal = () => {
    setOpenChangeProductionStatusModal(false);
    setIsMoveToReady(false);
  };
  const onClickOpenChangeProductionStatusModal = (order) => {
    setSelectedDocument(order);
    setOpenChangeProductionStatusModal(true);
  };

  // Inventory Intake Modal State
  const [openInventoryIntakeModal, setOpenInventoryIntakeModal] =
    useState(false);
  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [intakeHistory, setIntakeHistory] = useState<any[]>([]);
  const [intakeHistoryLoading, setIntakeHistoryLoading] = useState(false);
  const [openIntakeHistoryModal, setOpenIntakeHistoryModal] = useState(false);
  const [openPurchaseOrderTypeModal, setOpenPurchaseOrderTypeModal] =
    useState(false);

  const onClickOpenInventoryIntakeModal = async () => {
    setOpenInventoryIntakeModal(true);
    setInventoryLoading(true);

    const purchaseOrderId = quoteItemValue?.id;
    const inventoryParams = {
      documentType: docType,
      documentId: purchaseOrderId,
      purchaseOrderId,
    };

    const inventoryRes = await new Promise<any>((resolve) => {
      void getDocumentInventoryApi(callApi, resolve, inventoryParams);
    });

    if (inventoryRes?.success) {
      setInventoryData(inventoryRes?.data || []);
    } else {
      alertFault(t("alerts.failedToLoadData"));
    }

    setInventoryLoading(false);
  };

  const onClickOpenIntakeHistoryModal = async () => {
    const purchaseOrderId = quoteItemValue?.id;
    if (!purchaseOrderId) {
      return;
    }

    setOpenIntakeHistoryModal(true);
    setIntakeHistoryLoading(true);

    await getPurchaseOrderIntakeHistoryApi(
      callApi,
      (historyRes) => {
        if (historyRes?.success) {
          setIntakeHistory(historyRes?.data || []);
        } else {
          setIntakeHistory([]);
        }
        setIntakeHistoryLoading(false);
      },
      { purchaseOrderId },
    );
  };

  const onClickCloseIntakeHistoryModal = () => {
    setOpenIntakeHistoryModal(false);
    setIntakeHistory([]);
  };

  const onClickCloseInventoryIntakeModal = () => {
    setOpenInventoryIntakeModal(false);
    setInventoryData([]);
  };

  const { CheckPreferenceValue, GetPreferenceValueKey, GetPreferenceKey } =
    usePrintHousePreferences();

  const source = isQuoteConfirmation ? quoteConfirm : quoteItemValue;
  const showDetails =
    source?.showDetails !== ShowDocumentDetailsEnum.HideDetails;

  const showPrices = source?.showPrices !== ShowPricesEnum.HidePrices;
  const showItemDiscountColumn = !source?.hideDiscountColumn;
  const showPackagesAmount =
    isDeliveryNote && CheckPreferenceValue(EPreferences.SHOW_PACKAGES_AMOUNT);
  const normalizedPackagesAmountItems = (items = []) =>
    items.map((item) => ({
      ...item,
      packagesAmount:
        item?.packagesAmount ??
        item?.packagesQuantity ??
        item?.packageQuantity ??
        0,
    }));
  const getDocumentItemsForCreatePayload = () => {
    const defaultLanguageItems =
      normalizeDocumentItemsDetailsByDefaultLanguage(
        quoteItemValue?.documentItems || [],
        quoteItemValue?.defaultLanguage,
      ) || [];

    const itemsWithDefaultCode = defaultLanguageItems.map((item: any) => {
      const productCode = item?.productCode || item?.code;
      if (productCode) {
        return item;
      }
      return {
        ...item,
        productCode: "001",
        code: "001",
      };
    });

    return isDeliveryNote
      ? normalizedPackagesAmountItems(itemsWithDefaultCode)
      : itemsWithDefaultCode;
  };

  const isCanIntake = source?.isCanIntake === true;
  const isMaterialPurchaseOrder = useMemo(() => {
    if (!isPurchaseOrder) return false;
    if (isNewCreation && isMaterial) return true;

    const poType = source?.type ?? source?.Type;
    if (
      poType === PurchaseOrderType.Materials ||
      poType === "Materials" ||
      poType === 1
    ) {
      return true;
    }

    return (
      source?.documentItems?.some((item: any) => item.printHouseMaterialId) ??
      false
    );
  }, [
    isPurchaseOrder,
    isNewCreation,
    isMaterial,
    source?.type,
    source?.Type,
    source?.documentItems,
  ]);

  const tableHeaders = [
    "#",
    isDeliveryNote || isInvoice ? t("products.profits.documentNumber") : null,
    !CheckPreferenceValue(EPreferences.HIDE_ITEM_CODE_FROM_DOCUMENT_CART) &&
    t("sales.quote.itemCode"),
    t("products.profits.itemName"),
    showDetails ? t("products.profits.details") : null,
    showPackagesAmount ? t("sales.quote.packagesAmount") : null,
    isMaterialPurchaseOrder ? t("sales.purchaseOrder.packagesQuantity") : null,
    isMaterialPurchaseOrder ? t("sales.purchaseOrder.quantityInPackage") : null,
    isMaterialPurchaseOrder
      ? t("sales.purchaseOrder.totalQuantity")
      : t("sales.quote.amount"),
    isMaterialPurchaseOrder ? t("sales.purchaseOrder.unit") : null,
    showPrices ? t("products.profits.pricingListWidget.unitPrice") : null,
    showPrices && showItemDiscountColumn ? t("sales.quote.discount") : null,
    showPrices ? t("products.offsetPrice.admin.finalPrice") : null,
    isHp ? t(" ") : t("products.profits.more"),
  ].filter(Boolean);

  const columnWidths = isMaterialPurchaseOrder
    ? [
      "5%", // "#"
      "9%", // Item Code
      "10%", // Item Name
      "15%", // Details column (optional)
      "9%", // Amount
      "9%", // Amount
      "9%", // Amount
      "5%", // Unit Price
      showPrices ? "8%" : null, // Unit Price
      showPrices && showItemDiscountColumn ? "8%" : null, // Discount
      showPrices ? "8%" : null, // Final Price
      isHp ? "1%" : "5%", // More
    ]
    : [
      "5%", // "#"
      isDeliveryNote || isInvoice ? "10%" : null, // Document Number
      !CheckPreferenceValue(EPreferences.HIDE_ITEM_CODE_FROM_DOCUMENT_CART)
        ? "10%"
        : null, // Item Code
      "12%", // Item Name
      source?.showDetails === ShowDocumentDetailsEnum.All
        ? "25%"
        : source?.showDetails === ShowDocumentDetailsEnum.ShowJobNameOnly
          ? "10%"
          : null, // Details column (optional)
      showPackagesAmount ? "10%" : null, // Packages Amount column (optional)
      "10%", // Amount
      showPrices ? "10%" : null, // Unit Price
      showPrices && showItemDiscountColumn ? "10%" : null, // Discount
      showPrices ? "10%" : null, // Final Price
      isHp ? "1%" : "8%", // More
    ].filter(Boolean);

  // const columnWidths = ["5%", "10%", "12%", "25%", "10%", "10%", "10%", "10%"];
  const headerHeight = "44px";

  const updateDueDate = async (dueDateOverride?: string | null) => {
    const dueDateToSave = dueDateOverride ?? selectDate;
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.dueDate = dueDateToSave;
      updatedQuoteItemValue.taxDate = taxDate;
      updatedQuoteItemValue.creationDate = creationDate;
      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      // Store the old date value before making the API call
      const oldDueDate = quoteItemValue?.dueDate;

      const callBack = (res) => {
        if (res?.success) {
          alertSuccessAdded();
          getQuote();
          if (isQuote && !isQuoteConfirmation) {
            getQuoteDeliveryDate();
          }
        } else {
          if (
            res?.errors?.message === "delivrey_date_less_than_est" ||
            res?.statusCode === 116 ||
            res?.statusCode === 100 ||
            res?.errors?.message === "date_not_valid"
          ) {
            const errorMessage = t(`alerts.${res?.errors?.message}`);
            alertFault(errorMessage);
          } else {
            alertFaultUpdate();
          }
          // Restore the date that was on screen before this edit, not the system date.
          deliveryDateSourceRef.current = sourceBeforeEditRef.current;
          setSystemDueDate(systemDueBeforeEditRef.current);
          setUserDueDate(userDueBeforeEditRef.current);
          setSelectDate(dateBeforeEditRef.current ?? oldDueDate);
          deliveryDatesRef.current = {
            systemDueDate: systemDueBeforeEditRef.current,
            userDueDate: userDueBeforeEditRef.current,
          };
        }
      };
      await updateDueDateApi(callApi, callBack, {
        DocumentType: docType,
        Date: {
          documentId: quoteItemValue?.id,
          dueDate: dueDateToSave,
        },
      });
    }
  };
  const [estimatedProductionDate, setEstimatedProductionDate] = useState(null);
  const [systemDueDate, setSystemDueDate] = useState<string | null>(null);
  const [userDueDate, setUserDueDate] = useState<string | null>(null);
  const [needExampleDate, setNeedExampleDate] = useState<boolean>(false);
  deliveryDatesRef.current = { systemDueDate, userDueDate };

  const syncDeliveryDates = (
    dueDate: string | null | undefined,
    incomingUserDueDate: string | null | undefined,
    userUpdated: boolean,
  ) => {
    const retained = retainDeliveryDatePair(
      deliveryDatesRef.current,
      dueDate,
      incomingUserDueDate,
      userUpdated,
    );
    const nextSystem = retained.isUserDefined ? retained.systemDueDate : null;
    deliveryDatesRef.current = {
      systemDueDate: nextSystem,
      userDueDate: retained.userDueDate,
    };
    if (!retained.isUserDefined) {
      deliveryDateSourceRef.current = "user";
    }
    setSystemDueDate(nextSystem);
    setUserDueDate(retained.userDueDate);
    setSelectDate(
      displayedDeliveryDate(retained, deliveryDateSourceRef.current),
    );
  };

  const commitDeliveryDateChange = (formattedDueDate: string | null) => {
    if (
      formattedDueDate &&
      selectDate &&
      formattedDueDate.replace("T", " ") === selectDate.replace("T", " ")
    ) {
      return;
    }
    if (!activeClickAway) {
      dateBeforeEditRef.current = selectDate;
      userDueBeforeEditRef.current = userDueDate;
      systemDueBeforeEditRef.current = systemDueDate;
      sourceBeforeEditRef.current = deliveryDateSourceRef.current;
    }
    deliveryDateSourceRef.current = "user";
    const nextSystem =
      systemDueDate ||
      (selectDate && formattedDueDate && selectDate !== formattedDueDate
        ? selectDate
        : systemDueDate);
    deliveryDatesRef.current = {
      systemDueDate: nextSystem,
      userDueDate: formattedDueDate,
    };
    setSystemDueDate(nextSystem);
    setUserDueDate(formattedDueDate);
    setSelectDate(formattedDueDate);
    setActiveClickAway(true);
  };

  const selectDeliveryDateSource = (source: DeliveryDateSource) => {
    const stored = source === "system" ? systemDueDate : userDueDate;
    if (!stored) return;
    const next = formatDeliveryDateForSave(stored);
    if (
      selectDate &&
      next.replace("T", " ") === selectDate.replace("T", " ")
    ) {
      return;
    }
    dateBeforeEditRef.current = selectDate;
    userDueBeforeEditRef.current = userDueDate;
    systemDueBeforeEditRef.current = systemDueDate;
    sourceBeforeEditRef.current = deliveryDateSourceRef.current;
    deliveryDateSourceRef.current = source;
    setSelectDate(next);
    setActiveClickAway(false);
    updateDueDate(next);
  };

  const applyDeliveryDates = (data?: DocumentDeliveryDateData | null) => {
    syncDeliveryDates(
      data?.dueDate,
      data?.userDueDate,
      Boolean(quoteItemValue?.dueDateUpdatedByUser),
    );
  };
  const getQuoteDeliveryDate = async () => {
    const callBack = (res) => {
      if (!res?.success) return;
      const data = res?.data as DocumentDeliveryDateData | undefined;
      const needExample = data?.needExample ?? false;
      if (isDeliveryDateRequiredInQuote && !isUseJobsTimeline && !needExample) {
        if (data?.userDueDate) {
          syncDeliveryDates(
            data?.dueDate,
            data?.userDueDate,
            Boolean(quoteItemValue?.dueDateUpdatedByUser),
          );
        }
        return;
      }
      setNeedExampleDate(needExample);
      if (needExample) {
        // When BE flags that an example is needed, hide the delivery and
        // estimated production dates and don't pre-fill them.
        setSelectDate(null);
        setEstimatedProductionDate(null);
        setSystemDueDate(null);
        setUserDueDate(null);
        deliveryDatesRef.current = { systemDueDate: null, userDueDate: null };
        deliveryDateSourceRef.current = "user";
        return;
      }
      syncDeliveryDates(
        data?.dueDate,
        data?.userDueDate,
        Boolean(quoteItemValue?.dueDateUpdatedByUser),
      );
      setEstimatedProductionDate(data?.estimatedProductionTime ?? null);
    };
    await getDocumentDeliveryDateApi(callApi, callBack);
  };

  // For quote confirmation, only get estimated production date (not delivery date)
  const getEstimatedProductionDate = async () => {
    const callBack = (res) => {
      if (res?.success) {
        const data = res?.data as DocumentDeliveryDateData | undefined;
        // Only set estimated production date, don't override delivery date
        setEstimatedProductionDate(data?.estimatedProductionTime ?? null);
      }
    };
    await getDocumentDeliveryDateApi(callApi, callBack);
  };

  const updateOccasionalClientName = async (value) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.occasionalClientName = value;
      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
        } else {
          alertFaultUpdate();
        }
      };

      await updateOccasionalClientNameApi(callApi, callBack, {
        documentType: docType,
        document: {
          documentId: quoteItemValue?.id,
          occasionalClientName: clientName,
        },
      });
    }
  };

  const onBlurTitle = async (value) => {
    updateDocumentTitle(value);
    setIsUpdateDocumentTitle(null);
  };

  const onBlurClientName = async (value) => {
    updateOccasionalClientName(value);
    setIsUpdateClientName(null);
  };

  const onBlurBusinessCode = async () => {
    setIsUpdateBusinessCode(null);
  };

  const onBlurAddress = async () => {
    setIsUpdateAddress(null);
  };

  const onBlurAgent = async () => {
    setIsUpdateAgent(null);
  };

  const onBlurCustomerSupportRepresentative = async () => {
    setIsUpdateCustomerSupportRepresentative(null);
  };

  const onBlurBusinessName = async () => {
    setIsUpdateBusinessName(null);
  };

  const onBlurExchangeRate = async (value: number) => {
    updateExchangeRate(value);
    setIsUpdateExchangeRate(null);
  };

  const onBlurCurrency = async () => {
    setIsUpdateCurrency(null);
  };

  const onBlurVatStatus = async () => {
    setIsUpdateVatStatus(null);
  };
  const onBlurDefaultLanguage = async () => {
    setIsUpdateDefaultLanguage(null);
  };

  const getAllEmployees = useCallback(async () => {
    await getAndSetAllEmployees(callApi, setAgentListValue, {
      isAgent: true,
    }, false);
  }, [callApi]);

  const getCustomerSupportRepresentatives = useCallback(async () => {
    await getAndSetAllEmployees(
      callApi,
      setCustomerSupportRepresentativeListValue,
      {
        isCustomerSupportRepresentative: true,
      },
      false,
    );
  }, [callApi]);

  const updateAgent = async (item: any) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.agentId = item.value;
      updatedQuoteItemValue.agent = {
        firstname: item?.text,
        id: item?.value,
        lastname: item?.text,
      };
      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          setIsUpdateAgent(null);
          getQuote();
        } else {
          alertFaultUpdate();
        }
      };
      await updateAgentApi(callApi, callBack, {
        documentType: docType,
        document: {
          documentId: quoteItemValue?.id,
          agentId: item?.value,
        },
      });
    }
  };

  const updateCustomerSupportRepresentative = async (item: any) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.customerSupportRepresentativeId = item.value;
      updatedQuoteItemValue.customerSupportRepresentative = {
        firstname: item?.text,
        id: item?.value,
        lastname: item?.text,
      };
      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          setIsUpdateCustomerSupportRepresentative(null);
          getQuote();
        } else {
          alertFaultUpdate();
        }
      };
      await updateCustomerSupportRepresentativeApi(callApi, callBack, {
        documentType: docType,
        document: {
          documentId: quoteItemValue?.id,
          customerSupportRepresentativeId: item?.value,
        },
      });
    }
  };

  const updateExchangeRate = async (value: number) => {
    if (docType === DOCUMENT_TYPE.receipt && isNewCreation) {
      updateCurrency(quoteItemValue.currency, value).then();
      return;
    }

    if ((isInvoice || isDeliveryNote) && isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      // updatedQuoteItemValue.exchangeRate = value;

      setQuoteItemValue(updatedQuoteItemValue);
      setIsUpdateExchangeRate(null);

      // A document in the system currency always has a rate of 1, so a typed rate is ignored.
      if (
        isSameCurrency(
          quoteItemValue?.currency,
          quoteItemValue?.systemCurrency || systemCurrency,
        )
      ) {
        return;
      }

      if (
        !quoteItemValue?.documentItems ||
        quoteItemValue.documentItems.length === 0
      ) {
        return;
      }

      getCalculateQuoteItem(
        updatedQuoteItemValue,
        ItemCalculationType.ExchangeRate,
        value,
        updatedQuoteItemValue,
      );
      return;
    }

    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        setIsUpdateExchangeRate(null);
        getQuote();
      } else {
        alertFaultUpdate();
      }
    };
    await updateExchangeRateApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
        exchangeRate: value,
      },
    });
  };

  const refreshExchangeRate = async () => {
    if ((isInvoice || isDeliveryNote) && isNewCreation) {
      // For new creations, fetch exchange rate from API
      // Always system currency -> document currency. `fromCurrency` is the previous document
      // currency and must not be used as the base of the rate.
      const fromCurrency = getExchangeRateFromCurrency(
        quoteItemValue?.systemCurrency,
        systemCurrency,
      );
      const toCurrency = quoteItemValue?.currency;

      if (!fromCurrency || !toCurrency) {
        return;
      }

      // A document in the system currency always has a rate of 1; nothing to refresh.
      if (isSameCurrency(fromCurrency, toCurrency)) {
        return;
      }

      const callBack = (res) => {
        if (res?.success && res?.data?.data?.data) {
          const newExchangeRate = res.data?.data?.data;
          const updatedQuoteItemValue = { ...quoteItemValue };
          // updatedQuoteItemValue.exchangeRate = newExchangeRate;

          setQuoteItemValue(updatedQuoteItemValue);

          if (
            quoteItemValue?.documentItems &&
            quoteItemValue.documentItems.length > 0
          ) {
            getCalculateQuoteItem(
              updatedQuoteItemValue,
              ItemCalculationType.ExchangeRate,
              newExchangeRate,
              updatedQuoteItemValue,
            );
          }
        } else {
          alertFaultUpdate();
        }
      };

      await callApi(
        EHttpMethod.GET,
        `/v1/erp-service/currencies/get-exchange-rate`,
        {
          fromCurrency: fromCurrency,
          toCurrency: toCurrency,
        },
        false,
      ).then(callBack);
      return;
    }

    const callBack = (res) => {
      if (res?.success) {
        getQuote();
      } else {
        alertFaultUpdate();
      }
    };
    await refreshExchangeRateApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
      },
    });
  };

  const updateCurrency = async (
    currency: string,
    exchangeRate: number = null,
  ) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        setIsUpdateCurrency(null);
        if (!isNewCreation) getQuote();
      } else {
        alertFaultUpdate();
      }
    };

    const callBackIsNewReceipt = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        setIsUpdateCurrency(null);
        const newData = res.data;
        const updatedQuoteItemValue = cloneDeep(quoteItemValue);
        updatedQuoteItemValue.currency = newData.currency;
        updatedQuoteItemValue.systemCurrency = newData.systemCurrency;
        updatedQuoteItemValue.exchangeRate = newData.exchangeRate;
        for (let i = 0; i < updatedQuoteItemValue.receiptItems.length; i++) {
          const newItem = newData.receiptItems.find(
            (ri) =>
              ri.docEntry === updatedQuoteItemValue.receiptItems[i].docEntry &&
              ri.docLine === updatedQuoteItemValue.receiptItems[i].docLine,
          );
          if (newItem != null) {
            updatedQuoteItemValue.receiptItems[i].sumApplied =
              newItem.sumApplied;
          }
        }

        setQuoteItemValue(updatedQuoteItemValue);
      } else {
        alertFaultUpdate();
      }
    };

    if (isReceipt && isNewCreation) {
      await getClientPaymentItemsApi(
        callApi,
        callBackIsNewReceipt,
        {
          clientId: quoteItemValue?.customerID,
          documentNumber: router?.query?.documentNumber,
          currency: currency,
          exchangeRate: exchangeRate,
        },
        false,
      );
    } else if (
      (isInvoice || isDeliveryNote || isPurchaseInvoice || isPurchaseOrder) &&
      isNewCreation
    ) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.fromCurrency = quoteItemValue.currency;
      updatedQuoteItemValue.currency = currency;
      const houseCurrency =
        quoteItemValue?.systemCurrency || systemCurrency;
      if (houseCurrency) {
        // The exchange-rate input is only offered for a foreign currency. The rate itself is not
        // touched here: the calculate-item response returns it (1 for the system currency) and the
        // items are rescaled from the previous rate to that one.
        updatedQuoteItemValue.isForeignCurrency = !isSameCurrency(
          currency,
          houseCurrency,
        );
      } else if (
        idempotencyKeyFromQuery &&
        quoteItemValue?.currency &&
        currency !== quoteItemValue.currency
      ) {
        updatedQuoteItemValue.isForeignCurrency = true;
      }

      setQuoteItemValue(updatedQuoteItemValue);
      setIsUpdateCurrency(null);

      // if (
      //   !quoteItemValue?.documentItems ||
      //   quoteItemValue.documentItems.length === 0
      // ) {
      //   return; // Skip the API call
      // }

      getCalculateQuoteItem(
        updatedQuoteItemValue,
        ItemCalculationType.Currency,
        quoteItemValue.price,
        updatedQuoteItemValue,
      );
    } else {
      await updateDocumentCurrencyApi(callApi, callBack, {
        documentType: docType,
        document: {
          documentId: quoteItemValue?.id,
          currency: currency,
        },
      });
    }
  };

  const changeVatValueForVatStatus = (vatStatus: number) => {
    switch (vatStatus) {
      case EnumVATStatus.MustXPercents:
        return systemVAT;
      case EnumVATStatus.Exempt:
        return 0;
      default:
        return 0.18;
    }
  };

  const updateVatStatus = async (vatStatus: number) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.vatStatus = vatStatus;
      updatedQuoteItemValue.vat = changeVatValueForVatStatus(vatStatus);
      setQuoteItemValue(updatedQuoteItemValue);
      setIsUpdateVatStatus(null);
      getCalculateQuoteItem(
        updatedQuoteItemValue,
        ItemCalculationType.Discount,
        quoteItemValue.discount,
        updatedQuoteItemValue,
      );
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          setIsUpdateVatStatus(null);
          getQuote();
        } else {
          alertFaultUpdate();
        }
      };
      await updateDocumentVatStatusApi(callApi, callBack, {
        documentType: docType,
        document: {
          documentId: quoteItemValue?.id,
          vatStatus: vatStatus,
        },
      });
    }
  };

  const updateDefaultLanguage = async (defaultLanguage: string) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.defaultLanguage = defaultLanguage;
      setQuoteItemValue(updatedQuoteItemValue);
      setIsUpdateDefaultLanguage(null);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          setIsUpdateDefaultLanguage(null);
          getQuote();
        } else {
          alertFaultUpdate();
        }
      };
      await updateDocumentDefaultLanguageApi(callApi, callBack, {
        documentType: docType,
        documentId: quoteItemValue?.id,
        defaultLanguage: defaultLanguage,
      });
    }
  };

  const updateDocumentTitle = async (value: string) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      updatedQuoteItemValue.title = value;
      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          setIsUpdateDocumentTitle(null);
          getQuote();
        } else {
          alertFaultUpdate();
        }
      };
      await updateDocumentTitleApi(callApi, callBack, {
        documentType: docType,
        document: {
          documentId: quoteItemValue?.id,
          title: value,
        },
      });
    }
  };

  const alertExistPurchaseNumber = useModal();
  const orderUpdateConfirmationModal = useModal();
  const [openFinalPriceUpdateChoiceModal, setOpenFinalPriceUpdateChoiceModal] =
    useState(false);
  const finalPriceUpdateChoiceResolverRef = useRef<
    ((choice: FinalPriceUpdateChoice | null) => void) | undefined
  >(undefined);

  const settleFinalPriceUpdateChoice = (
    choice: FinalPriceUpdateChoice | null
  ) => {
    const resolver = finalPriceUpdateChoiceResolverRef.current;
    if (!resolver) {
      setOpenFinalPriceUpdateChoiceModal(false);
      return;
    }
    finalPriceUpdateChoiceResolverRef.current = undefined;
    setOpenFinalPriceUpdateChoiceModal(false);
    resolver(choice);
  };

  const askFinalPriceUpdateChoice = () =>
    new Promise<FinalPriceUpdateChoice | null>((resolve) => {
      finalPriceUpdateChoiceResolverRef.current = resolve;
      setOpenFinalPriceUpdateChoiceModal(true);
    });

  const getFinalPriceUpdateBehaviorPreference = () => {
    return (
      GetPreferenceValueKey(EPreferences.FINAL_PRICE_UPDATE_BEHAVIOR) ||
      GetPreferenceKey(EPreferences.FINAL_PRICE_UPDATE_BEHAVIOR) ||
      CheckPreferenceValue(EPreferences.FINAL_PRICE_UPDATE_BEHAVIOR)
    );
  };

  const resolveFinalPriceUpdateChoiceForItem = async (): Promise<
    FinalPriceUpdateChoice | undefined | false
  > => {
    const hideDiscountColumn = Boolean(source?.hideDiscountColumn);
    const resolvedChoice = resolveFinalPriceUpdateChoice({
      behavior: getFinalPriceUpdateBehaviorPreference(),
      hideDiscountColumn,
    });

    if (resolvedChoice === "ask") {
      const userChoice = await askFinalPriceUpdateChoice();
      if (!userChoice) {
        return false;
      }
      return userChoice;
    }

    return resolvedChoice;
  };
  // Track if table cells were updated (not via edit modal) - using Recoil atom
  const [hasTableCellUpdates, setHasTableCellUpdates] = useRecoilState(
    hasTableCellUpdatesState,
  );
  const orderUpdateSendModal = useModal();
  const [isOrderUpdateEmailComposer, setIsOrderUpdateEmailComposer] =
    useState(false);

  // Import sendPaymentState to populate it when opening order update modal
  const [sendPayment, setSendPayment] =
    useRecoilState<StatePayment>(sendPaymentState);

  const [originalPurchaseNumber, setOriginalPurchaseNumber] = useState(null);
  const [pendingPurchaseNumber, setPendingPurchaseNumber] = useState(null);

  // Detect URL parameter to show order update confirmation modal
  // useEffect(() => {
  //   if (
  //     router.query.showUpdateModal === "true" &&
  //     docType === DOCUMENT_TYPE.order &&
  //     quoteItemValue?.id &&
  //     !orderUpdateConfirmationModal.isOpen
  //   ) {
  //     orderUpdateConfirmationModal.open();
  //   }
  // }, [router.query.showUpdateModal, docType, quoteItemValue?.id, orderUpdateConfirmationModal]);

  const onBlurPurchaseNumber = async (value) => {
    const requiresSupplierForPurchaseNumber =
      docType === DOCUMENT_TYPE.purchaseOrder ||
      docType === DOCUMENT_TYPE.purchaseInvoice;

    if (!quoteItemValue?.client?.id && requiresSupplierForPurchaseNumber) {
      alertFault("home.admin.pleaseSelectSupplier");
      setIsUpdatePurchaseNumber(null);
      return;
    }

    const trimmedValue = (value ?? "").trim();
    const emptyPlaceholder =
      docType === DOCUMENT_TYPE.purchaseInvoice ||
        docType === DOCUMENT_TYPE.purchaseInvoiceRefund
        ? t("sales.cart.noInvoiceNumber")
        : t("sales.quote.noPurchaseNumber");
    const normalizedValue =
      trimmedValue && trimmedValue !== emptyPlaceholder ? trimmedValue : null;

    // Prevent unnecessary API calls when nothing changed.
    if ((quoteItemValue?.purchaseNumber ?? null) === normalizedValue) {
      setIsUpdatePurchaseNumber(null);
      return;
    }

    if (normalizedValue) {
      const exists = await new Promise<boolean>((resolve) => {
        const callBack = (res) => {
          if (res?.success) {
            resolve(res?.data === true);
          } else {
            resolve(false);
          }
        };

        checkPurchaseNumberExistsApi(callApi, callBack, {
          documentType: docType,
          purchaseNumber: normalizedValue,
          documentId: quoteItemValue?.id,
          ...(requiresSupplierForPurchaseNumber && {
            supplierId: quoteItemValue?.client?.id,
          }),
        });
      });

      if (exists) {
        setOriginalPurchaseNumber(quoteItemValue?.purchaseNumber ?? null);
        setPendingPurchaseNumber(normalizedValue);
        setIsUpdatePurchaseNumber(null);
        alertExistPurchaseNumber.open();
        return;
      }
    }

    await updatePurchaseNumber(normalizedValue);
    setIsUpdatePurchaseNumber(null);
  };

  const updatePurchaseNumber = async (
    value: string | null,
    forceUpdate = false,
  ) => {
    if (isNewCreation) {
      setQuoteItemValue({ ...quoteItemValue, purchaseNumber: value });
      return;
    }
    const originalValue = quoteItemValue?.purchaseNumber ?? null;

    if (!forceUpdate) {
      setOriginalPurchaseNumber(originalValue);
      setPendingPurchaseNumber(value);
    }

    const callBack = (res) => {
      if (res?.success) {
        if (res?.data === false && !forceUpdate) {
          alertExistPurchaseNumber.open();
        } else {
          alertSuccessUpdate();
          setIsUpdatePurchaseNumber(null);
          getQuote();
        }
      } else {
        alertFaultUpdate();
        setQuoteItemValue({ ...quoteItemValue, purchaseNumber: originalValue });
      }
    };

    await updatePurchaseNumberApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
        purchaseNumber: value,
        ...(forceUpdate && { forceUpdate: true }),
      },
    });
  };

  useEffect(() => {
    if (
      router?.query?.isNewCreation &&
      docType === DOCUMENT_TYPE.receipt &&
      router?.query?.documentNumber &&
      router?.query?.ClientId
    ) {
      const clientId = { id: router.query.ClientId };
      onChangeSelectBusiness(clientId);
    }
  }, []);

  const [routerChangedFromClientSelect, setRouterChangedFromClientSelect] =
    useState(false);

  const onCloseVatMismatchModal = () => {
    setOpenVatMismatchModal(false);
  };

  const onConfirmVatMismatchModal = () => {
    setOpenVatMismatchModal(false);
  };

  const onChangeSelectBusiness = async (
    item: any,
    clientChangeSource: ClientChangeSource = ClientChangeSource.REGULAR_CHANGE,
    prevDocumentItem: any | null = null, //in case we change client and click "save and change client"
  ) => {
    if (router?.query?.isNewCreation && docType === DOCUMENT_TYPE.receipt) {
      setLoadingWithShadow(true);
      const callBack = (res) => {
        setLoadingWithShadow(false);
        if (res?.success) {
          const _data = res?.data || {};
          if (
            router?.query?.isNewCreation &&
            router?.query?.documentNumber &&
            router?.query?.ClientId
          ) {
            const documentNumber = router.query.documentNumber;
            if (documentNumber) {
              const foundRow = _data?.receiptItems?.find(
                (row) => row.docNum === documentNumber,
              );
              if (foundRow) {
                foundRow.isChecked = true;
              } else {
                // When changing the client from the page, we need to reset the router after changing from the payment button on the invoice cart page.
                const { documentNumber, ClientId, ...newQuery } = router.query;
                router.replace(
                  {
                    pathname: router.pathname,
                    query: { ...newQuery },
                  },
                  undefined,
                  { shallow: true },
                );
                setRouterChangedFromClientSelect(true);
              }
            }
          }
          setQuoteItemValue(_data);
          setIsUpdateBusinessName(null);
        } else {
          alertFaultAdded();
        }
        getAllCreditCardTransactions(item?.id);
      };
      await getClientPaymentItemsApi(
        callApi,
        callBack,
        { clientId: item?.id, documentNumber: router?.query?.documentNumber },
        false,
      );
    } else if (router?.query?.isNewCreation) {
      const res = await callApi(
        EHttpMethod.POST,
        `/v1/erp-service/documents/get-new-document-data`,
        {
          documentType: docType,
          clientId: item?.id,
          ...(router?.query?.budgetId
            ? { budgetId: router.query.budgetId }
            : {}),
        },
      );

      if (res?.success) {
        const _data = res?.data?.data?.result || {};
        applyClientAgentToDocument(_data, item?.agentId || item?.AgentId);
        const isNewCreation = router?.query?.isNewCreation === "true";

        if (clientChangeSource === ClientChangeSource.SAVE_AND_START_NEW_DOC) {
          const {
            orderId,
            deliveryNoteId,
            documentId,
            documentToDuplicateId,
            ...newQuery
          } = router.query;

          router.replace(
            {
              pathname: router.pathname,
              query: { ...newQuery },
            },
            undefined,
            { shallow: true },
          );

          setRouterChangedFromClientSelect(true);
        }
        if (
          clientChangeSource === ClientChangeSource.SAVE_AND_CHANGE_CLIENT &&
          prevDocumentItem
        ) {
          setQuoteItemValue({
            ..._data,
            documentItems: prevDocumentItem?.documentItems,
            totalPrice: prevDocumentItem?.totalPrice,
            totalPayment: prevDocumentItem?.totalPayment,
            discount: prevDocumentItem?.discount,
            discountAmount: prevDocumentItem?.discountAmount,
            totalVAT: prevDocumentItem?.totalVAT,
          });
        } else if (
          prevDocumentItem &&
          prevDocumentItem?.documentItems &&
          Array.isArray(prevDocumentItem.documentItems) &&
          prevDocumentItem.documentItems.length > 0
        ) {
          // Merge re-calculated items from supplier change
          const updatedQuoteItemValue = {
            ..._data,
            documentItems: prevDocumentItem.documentItems,
          };
          setQuoteItemValue(updatedQuoteItemValue);

          // Recalculate totals after updating document items
          if (isNewCreation) {
            const calculateRes = await callApi(
              EHttpMethod.POST,
              `/v1/erp-service/documents/calculate-document-new`,
              {
                documentType: docType,
                document: {
                  exchangeRate:
                    updatedQuoteItemValue?.exchangeRate === 0
                      ? 1
                      : updatedQuoteItemValue?.exchangeRate,
                  totalPrice: updatedQuoteItemValue?.totalPrice,
                  data:
                    updatedQuoteItemValue?.discountAmount > -1 &&
                      updatedQuoteItemValue?.discountAmount < 1
                      ? 0
                      : updatedQuoteItemValue?.discount || 0,
                  calculationType: 0,
                  totalPriceAfterDiscount:
                    updatedQuoteItemValue?.totalPriceAfterDiscount,
                  discount: updatedQuoteItemValue?.discount || 0,
                  discountAmount: updatedQuoteItemValue?.discountAmount || 0,
                  totalPayment: updatedQuoteItemValue?.totalPayment,
                  vat: updatedQuoteItemValue?.vat,
                  vatStatus: quoteItemValue?.vatStatus,
                  totalVAT: updatedQuoteItemValue?.totalVAT,
                  roundPrices: updatedQuoteItemValue?.client?.roundPrices,
                  documentItems: updatedQuoteItemValue.documentItems?.map(
                    (item) => ({
                      finalPrice: item.finalPrice,
                    }),
                  ),
                },
              },
            );
            if (calculateRes?.success) {
              const calculatedData = calculateRes?.data?.data?.data;
              setQuoteItemValue({
                ...updatedQuoteItemValue,
                discount: calculatedData.discount,
                discountAmount: calculatedData.discountAmount,
                totalPayment: calculatedData.totalPayment,
                totalPrice: calculatedData.totalPrice,
                totalPriceAfterDiscount: calculatedData.totalPriceAfterDiscount,
                totalVAT: calculatedData.totalVAT,
                vat: calculatedData.vat,
              });
            }
          }
        } else {
          setQuoteItemValue(_data);
        }

        // Check if the client vat Exempt and show modal if needed
        const isCorrectPage =
          router.pathname === "/invoice" || router.pathname === "/deliveryNote";
        const isVATMismatch = Math.abs(systemVAT - _data?.vat) > 0.001;

        if (
          isNewCreation &&
          isCorrectPage &&
          isVATMismatch &&
          shouldShowSystemVatMismatchWarning(
            printHouseProfileState?.erpType,
            router.pathname,
          )
        ) {
          setOpenVatMismatchModal(true);
        }
        setIsUpdateBusinessName(null);
      } else {
        alertFaultAdded();
      }
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          setIsUpdateBusinessName(null);
          getQuote();
        } else {
          alertFaultUpdate();
        }
      };
      await changeDocumentClientApi(callApi, callBack, {
        documentType: docType,
        client: {
          documentID: quoteItemValue?.id,
          clientId: item?.id,
        },
      });
    }
  };

  const onBlurContactName = async () => {
    setIsUpdateContactName(null);
    //  setIsDisplayWidget(false);
  };

  const onBlurContactEmail = async () => {
    setIsUpdateContactEmail(null);
  };

  const onBlurContactMobile = async () => {
    setIsUpdateContactMobile(null);
  };

  const changeItems = (index: number, filedName: string, value: any) => {
    let temp = [...items];
    temp[index] = {
      ...temp[index],
      [filedName]: value,
    };
    setItems(temp);
  };

  const onClickAddNewContact = async () => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      if (!Array.isArray(updatedQuoteItemValue.documentContacts)) {
        updatedQuoteItemValue.documentContacts = [];
      }
      const newContact = {
        id: uuidv4(),
        contactID: selectedContactById?.id,
        contactName: selectedContactById?.name,
        contactMail: selectedContactById?.mail,
        contactPhone: selectedContactById?.phone,
        documentID: quoteItemValue?.id,
      };
      updatedQuoteItemValue.documentContacts = [
        ...updatedQuoteItemValue.documentContacts,
        newContact,
      ];
      setQuoteItemValue(updatedQuoteItemValue);
      setIsDisplayWidget(false);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessAdded();
          setIsDisplayWidget(false);
          getQuote();
        } else {
          alertFaultAdded();
        }
      };
      await addDocumentContactApi(callApi, callBack, {
        documentType: docType,
        contact: {
          contactID: selectedContactById?.id,
          contactName: selectedContactById?.name,
          contactMail: selectedContactById?.mail,
          contactPhone: selectedContactById?.phone,
          documentID: quoteItemValue?.id,
        },
      });
    }
  };

  const updateClientContact = async (item: any) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      const updatedContacts = updatedQuoteItemValue.documentContacts.map(
        (contact: any) => {
          if (contact.id === item.id) {
            return item;
          }
          return contact;
        },
      );

      updatedQuoteItemValue.documentContacts = updatedContacts;
      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          setIsUpdateContactEmail(null);
          setIsUpdateContactName(null);
          setIsUpdateContactMobile(null);
          getQuote();
        } else {
          alertFaultUpdate();
        }
      };
      await updateDocumentContactApi(callApi, callBack, {
        documentType: docType,
        contact: {
          id: item?.id,
          contactID: item?.contactID,
          contactName: item?.contactName,
          contactMail: item?.contactMail,
          contactPhone: item?.contactPhone,
          documentID: quoteItemValue?.id,
        },
      });
    }
  };

  const onClickDeleteContact = async (item: any) => {
    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      const updatedContacts = updatedQuoteItemValue.documentContacts.filter(
        (contact: any) => {
          return contact.id !== item.id;
        },
      );
      updatedQuoteItemValue.documentContacts = updatedContacts;
      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessDelete();
          onCloseDeleteModalContact();
          getQuote();
        } else {
          alertFaultDelete();
        }
      };
      await deleteDocumentContactApi(callApi, callBack, {
        documentType: docType,
        documentContactId: item?.id,
      });
    }
  };

  const contactsListRef = useRef<HTMLDivElement>(null);

  const handleShowMore = () => {
    setDisplayedItems(items.length); // or whatever logic you have
    setTimeout(() => {
      contactsListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 100); // small timeout to allow the new items to render
  };

  const handleShowLess = () => {
    setDisplayedItems(2);
  };

  const onChangeUpdateClientContact = useCallback(
    (filedName: string, value: any) => {
      setSelectedContactById((prev) => {
        return {
          ...prev,
          [filedName]: value,
        };
      });
    },
    [selectedContactById],
  );

  const onInputChangeMail = (v: any) => {
    onChangeUpdateClientContact("mail", v);
  };

  const onInputChangePhone = (v: any) => {
    onChangeUpdateClientContact("phone", v);
  };

  const onOpenDeleteModalContact = (item) => {
    setSelectedContact(item);
    setOpenDeleteModalContact(true);
  };
  const onCloseDeleteModalContact = () => {
    setOpenDeleteModalContact(false);
  };

  // State for add item restriction dialog
  const [showAddItemRestrictionDialog, setShowAddItemRestrictionDialog] =
    useState(false);

  const onOpenNewItem = () => {
    // Check if it's a material purchase order and supplier is required
    if (isPurchaseOrder && isNewCreation && !quoteItemValue?.client?.id) {
      alertFault(t("home.admin.pleaseSelectSupplier"));
      return;
    }

    // Check if purchase order status is Closed or Received
    if (isPurchaseOrder && !isNewCreation) {
      const isClosed =
        quoteItemValue?.statusTitleText === StatusTitleText.PurchaseOrderClosed;
      const isReceived =
        quoteItemValue?.documentStatus === PurchaseOrderStatuses.Received;

      if (isClosed || isReceived) {
        setShowAddItemRestrictionDialog(true);
        return;
      }
    }

    setOpenAddNewItemModal(true);
  };

  // Handle create new purchase order - open type selection modal
  const handleCreateNewPurchaseOrder = () => {
    setShowAddItemRestrictionDialog(false);
    // Open purchase order type selection modal (same as "Create new" button)
    onOpenPurchaseOrderTypeModal();
  };

  // Handle cancel - check if can save with same supplier
  const handleCancelAddItemRestriction = () => {
    setShowAddItemRestrictionDialog(false);
    // If supplier exists, we could potentially save, but user chose not to create new PO
    // Just close the dialog - no action needed as per requirements
  };

  // Handle change status to Under Creation to allow adding items
  const handleChangeStatusToUnderCreation = () => {
    setShowAddItemRestrictionDialog(false);
    // Change status only — do not undo intake (explicit false skips undo dialog)
    updatePurchaseOrderStatus(PurchaseOrderStatuses.UnderCreation, false);
  };
  const onOpenNewContact = () => {
    setOpenAddNewContactModal(true);
  };
  const onCloseCopyFromOrder = () => {
    setOpenCopyFromOrderModal(false);
  };

  const [copyFromDocumentType, setCopyFromDocumentType] =
    useState<DOCUMENT_TYPE>();

  const onOpenCopyFromOrder = (documentNum: DOCUMENT_TYPE) => {
    setCopyFromDocumentType(documentNum);
    setOpenCopyFromOrderModal(true);
  };

  const onCloseNewItem = () => {
    setOpenAddNewItemModal(false);
    // Clear table cell update flag when item edit modal is closed
    setHasTableCellUpdates(false);
  };
  const onCloseNewContact = () => {
    setOpenAddNewContactModal(false);
  };
  const [selectedFirstMaterial, setSelectedFirstMaterial] = useRecoilState<any>(
    selectedFirstMaterialState,
  );
  const [isUpdatingDocumentPrices, setIsUpdatingDocumentPrices] =
    useState(false);
  const pendingPriceUpdatesRef = useRef(0);

  const startDocumentPricesUpdateLock = () => {
    pendingPriceUpdatesRef.current += 1;
    setIsUpdatingDocumentPrices(true);
  };

  const finishDocumentPricesUpdateLock = () => {
    pendingPriceUpdatesRef.current = Math.max(
      0,
      pendingPriceUpdatesRef.current - 1,
    );
    if (pendingPriceUpdatesRef.current === 0) {
      setIsUpdatingDocumentPrices(false);
    }
  };

  const getCalculateQuoteItem = async (
    quoteItem: any,
    calculationType: number,
    data: number,
    newUpdatedQuoteItemValue?: any,
    isPrintHouseMaterialItem?: boolean,
  ) => {
    let finalPriceUpdateChoice: FinalPriceUpdateChoice | undefined;
    if (calculationType === ItemCalculationType.finalPrice) {
      const resolvedChoice = await resolveFinalPriceUpdateChoiceForItem();
      if (resolvedChoice === false) {
        revertUnsavedFinalPrice(quoteItem);
        return;
      }
      finalPriceUpdateChoice = resolvedChoice;
    }

    startOptimisticTaxCalculation();
    startDocumentPricesUpdateLock();
    try {
      if (isNewCreation) {
        const newQuoteItemValue = newUpdatedQuoteItemValue ?? quoteItemValue;
        const quoteItemEdit = newQuoteItemValue.documentItems.find(
          (item) => item.id === quoteItem.id,
        );
        const tableItem = documentItems.find(
          (item) => item.id === quoteItem.id,
        );
        const resolvedPrevFinalPrice = resolveItemPrevFinalPrice(
          quoteItem,
          quoteItemEdit,
          tableItem,
          calculationType,
        );

        // Prepare document payload
        const documentPayload: any = {
          exchangeRate:
            newQuoteItemValue?.exchangeRate == 0
              ? 1
              : newQuoteItemValue?.exchangeRate,
          price: quoteItem?.price,
          discount: quoteItem?.discount,
          isPrintHouseMaterialItem:
            quoteItem?.printHouseMaterialId &&
              (isPrintHouseMaterialItem || selectedFirstMaterial)
              ? true
              : false,
          finalPrice: quoteItemEdit?.finalPrice,
          prevFinalPrice: resolvedPrevFinalPrice,
          quantity: quoteItem?.quantity,
          roundPrices: newQuoteItemValue?.client?.roundPrices,
          document: {
            totalPrice: newQuoteItemValue?.totalPrice,
            totalPriceAfterDiscount: newQuoteItemValue?.totalPriceAfterDiscount,
            discount: +newQuoteItemValue?.discount,
            discountAmount: newQuoteItemValue?.discountAmount,
            totalPayment: newQuoteItemValue?.totalPayment,
            vat: newQuoteItemValue?.vat,
            vatStatus: newQuoteItemValue?.vatStatus,
            totalVAT: newQuoteItemValue?.totalVAT,
          },
          data: data,
          calculationType: calculationType,
          currency: newQuoteItemValue?.currency,
          fromCurrency: newQuoteItemValue?.fromCurrency,
          ...(finalPriceUpdateChoice
            ? { finalPriceUpdateChoice }
            : {}),
        };

        // When updating PackagesQuantity, QuantityInPackage, or Amount for material items,
        // include packagesQuantity and quantityInPackage in the payload
        if (
          (calculationType === ItemCalculationType.PackagesQuantity ||
            calculationType === ItemCalculationType.QuantityInPackage ||
            calculationType === ItemCalculationType.Amount) &&
          quoteItem?.printHouseMaterialId
        ) {
          documentPayload.packagesQuantity =
            quoteItem?.packagesQuantity || quoteItem?.packageQuantity || 0;
          documentPayload.quantityInPackage =
            quoteItem?.quantityInPackage || quoteItem?.quantityPerPackage || 0;
        }

        if (isDraftInvoiceQuickBooksTaxEnabled) {
          Object.assign(documentPayload, {
            calculateQuickBooksTax: true,
            customerId: newQuoteItemValue?.customerID,
            clientCode: newQuoteItemValue?.client?.code,
            documentBillingAddresses:
              newQuoteItemValue?.documentBillingAddresses ?? [],
            documentDeliveryAddresses:
              newQuoteItemValue?.documentDeliveryAddresses ?? [],
            itemId: quoteItem.id,
            productID: quoteItem.productID ?? quoteItemEdit?.productID,
            code: quoteItem.code ?? quoteItemEdit?.code,
            productName: quoteItem.productName ?? quoteItemEdit?.productName,
            allDocumentItems: buildAllDocumentItemsForItemCalculation(
              newQuoteItemValue?.documentItems,
              quoteItem.id,
              calculationType,
              data,
              quoteItem,
            ),
          });
        }

        const res = await callApi(
          EHttpMethod.POST,
          `/v1/erp-service/documents/calculate-item`,
          {
            documentType: docType,
            document: documentPayload,
          },
          false,
        );
        if (res?.success) {
          const _data = res?.data?.data?.data;
          notifyPackagingOutdated(res);
          notifyPackagingOutdated(_data);
          // if is request to change the currency in document in creation mode just multiple the prices with exchange rate
          if (
            calculationType === ItemCalculationType.Currency ||
            calculationType === ItemCalculationType.ExchangeRate
          ) {
            let exchangeRate = _data.exchangeRate;
            let oldExchangeRate =
              quoteItem.exchangeRate === 0 ||
                quoteItem.exchangeRate === null ||
                quoteItem.exchangeRate === undefined
                ? 1
                : quoteItem.exchangeRate;

            const updatedQuoteItemValue = {
              ...newQuoteItemValue,
              discount: _data?.document?.discount,
              discountAmount: _data?.document?.discountAmount,
              totalPayment: _data?.document?.totalPayment,
              totalPrice: _data?.document?.totalPrice,
              totalPriceAfterDiscount: _data?.document?.totalPriceAfterDiscount,
              totalVAT: _data?.document?.totalVAT,
              vat: _data?.document?.vat,
              exchangeRate,
              documentItems: newQuoteItemValue.documentItems.map(
                (documentItem) => {
                  return {
                    ...documentItem,
                    finalPrice: (
                      (documentItem.finalPrice * exchangeRate) /
                      oldExchangeRate
                    ).toFixed(3),
                    price: (
                      (documentItem.price * exchangeRate) /
                      oldExchangeRate
                    ).toFixed(3),
                    discount: (
                      (documentItem.discount * exchangeRate) /
                      oldExchangeRate
                    ).toFixed(3),
                  };
                },
              ),
            };
            setQuoteItemValue(updatedQuoteItemValue);
          } else {
            const updatedQuoteItemValue = {
              ...newQuoteItemValue,
              discount: _data?.document?.discount,
              discountAmount: _data?.document?.discountAmount,
              totalPayment: _data?.document?.totalPayment,
              totalPrice: _data?.document?.totalPrice,
              totalPriceAfterDiscount: _data?.document?.totalPriceAfterDiscount,
              totalVAT: _data?.document?.totalVAT,
              vat: _data?.document?.vat,
              documentItems: newQuoteItemValue.documentItems.map(
                (documentItem) => {
                  if (documentItem.id === quoteItem.id) {
                    // Preserve packagesQuantity and quantityInPackage when updating PackagesQuantity or QuantityInPackage
                    const updatedItem: any = {
                      ...documentItem,
                      finalPrice: _data.finalPrice,
                      prevFinalPrice: _data.finalPrice,
                      price: _data.price,
                      quantity: _data.quantity,
                      discount: _data.discount,
                    };

                    // Preserve packagesQuantity and quantityInPackage if they exist in the response or in the original item
                    if (
                      calculationType ===
                      ItemCalculationType.PackagesQuantity ||
                      calculationType ===
                      ItemCalculationType.QuantityInPackage ||
                      calculationType === ItemCalculationType.Amount
                    ) {
                      updatedItem.packagesQuantity =
                        _data.packagesQuantity ??
                        quoteItem.packagesQuantity ??
                        quoteItem.packageQuantity ??
                        documentItem.packagesQuantity ??
                        documentItem.packageQuantity;
                      updatedItem.quantityInPackage =
                        _data.quantityInPackage ??
                        quoteItem.quantityInPackage ??
                        quoteItem.quantityPerPackage ??
                        documentItem.quantityInPackage ??
                        documentItem.quantityPerPackage;
                    }

                    return updatedItem;
                  } else {
                    return documentItem;
                  }
                },
              ),
            };
            if (isDraftInvoiceQuickBooksTaxEnabled) {
              updatedQuoteItemValue.taxComposition =
                _data?.document?.taxComposition ?? null;
              updatedQuoteItemValue.taxCalculating = false;
              applyDraftInvoiceDocumentState(updatedQuoteItemValue);
            } else {
              setQuoteItemValue(updatedQuoteItemValue);
            }
            setSelectedFirstMaterial(false);
          }
        } else {
          setSelectedFirstMaterial(false);
        }
      } else {
        // Mark that table cells were updated (not via edit modal)
        if (docType === DOCUMENT_TYPE.order) {
          setHasTableCellUpdates(true);
        }
        const callBack = (res) => {
          if (res?.success) {
            notifyPackagingOutdated(res);
            alertSuccessUpdate();
            getQuote();
          } else {
            if (res?.message === "amount_greater_budget") {
              alertFault(`${t("sales.quote.errorUpdateBudget")}`);
            } else {
              alertFaultUpdate();
            }
            getQuote();
          }
        };
        await calculateDocumentItemApi(callApi, callBack, {
          documentType: docType,
          ItemId: quoteItem?.id,
          data,
          calculationType,
          ...(finalPriceUpdateChoice
            ? { finalPriceUpdateChoice }
            : {}),
        });
      }
    } finally {
      finishDocumentPricesUpdateLock();
    }
  };

  const getCalculateQuoteConfirmationItem = async (
    printHouseId: string,
    ItemId: string,
    quantity?: number,
  ) => {
    startOptimisticTaxCalculation();
    startDocumentPricesUpdateLock();
    try {
      const documentItemIds = quoteConfirm?.documentItems
        ?.filter((x) => x.isChecked)
        ?.map((x) => x.id);
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          const newQuote = res?.data;
          newQuote.documentItems = newQuote.documentItems.map((item) => {
            item.isChecked = documentItemIds.includes(item.id);
            return item;
          });
          setQuoteConfirm(newQuote);
        } else {
          alertFaultUpdate();
          getQuote();
        }
      };
      await updateQuoteItemQuantityApi(callApi, callBack, {
        printHouseId,
        ItemId,
        quantity,
        documentItemIds: documentItemIds,
      });
    } finally {
      finishDocumentPricesUpdateLock();
    }
  };

  const onCloseDuplicateWithDifferentQTY = () => {
    setOpenDuplicateWithDifferentQTYModal(false);
  };
  const onOpenDuplicateWithDifferentQTY = () => {
    setOpenDuplicateWithDifferentQTYModal(true);
  };
  const onClickDuplicateWithDifferentQTY = (quoteItem) => {
    onOpenDuplicateWithDifferentQTY();
    setQuateItemId(quoteItem?.id);
    setQuoteItem(quoteItem);
  };
  const onOpenQuickDuplicate = () => {
    setOpenQuickDuplicateModal(true);
  };
  const onCloseQuickDuplicate = () => {
    setOpenQuickDuplicateModal(false);
  };
  const onClickQuickDuplicate = (quoteItem, isAddQuantity = false) => {
    onOpenQuickDuplicate();
    setQuateItemId(quoteItem?.id);
    setQuoteItem(quoteItem);
    setIsAddQuantity(isAddQuantity);
  };

  const duplicateQuoteItemWithAnotherQuantity = async () => {
    startOptimisticTaxCalculation();
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessAdded();
        onCloseDuplicateWithDifferentQTY();
        getQuote();
      } else {
        alertFaultAdded();
      }
    };
    await duplicateWithAnotherQuantityApi(callApi, callBack, {
      ItemId: quoteItemId,
      amount: parseInt(amountValue),
      documentType: docType,
    });
  };

  const onCloseDeleteItemModal = () => {
    setOpenDeleteItemModal(false);
  };

  const onOpenDeleteItemModal = () => {
    setOpenDeleteItemModal(true);
  };

  const deleteQuoteItem = async () => {
    if (isNewCreation) {
      startOptimisticTaxCalculation();
      const updatedDocumentItems = quoteItemValue.documentItems.filter(
        (item) => item.id !== quoteItemId,
      );
      const updatedQuoteItemValue = {
        ...quoteItemValue,
        documentItems: updatedDocumentItems,
      };
      const res = await callApi(
        EHttpMethod.POST,
        `/v1/erp-service/documents/calculate-document-new`,
        {
          documentType: docType,
          document: isDraftInvoiceQuickBooksTaxEnabled
            ? buildDraftCalculateDocumentPayload(updatedQuoteItemValue)
            : {
              exchangeRate: updatedQuoteItemValue?.exchangeRate || 1,
              totalPrice: updatedQuoteItemValue?.totalPrice,
              data:
                updatedQuoteItemValue?.discountAmount > -1 &&
                  updatedQuoteItemValue?.discountAmount < 1
                  ? 0
                  : updatedQuoteItemValue?.discount || 0,
              calculationType: 0,
              totalPriceAfterDiscount:
                updatedQuoteItemValue?.totalPriceAfterDiscount,
              discount: updatedQuoteItemValue?.discount,
              discountAmount: updatedQuoteItemValue?.discountAmount,
              totalPayment: updatedQuoteItemValue?.totalPayment,
              vat: updatedQuoteItemValue?.vat,
              totalVAT: updatedQuoteItemValue?.totalVAT,
              vatStatus: quoteItemValue?.vatStatus,
              roundPrices: quoteItemValue?.client?.roundPrices,
              documentItems: updatedQuoteItemValue?.documentItems.map((item) => ({
                finalPrice: item.finalPrice,
              })),
            },
        },
      );
      if (res?.success) {
        const _data = res?.data?.data?.data;
        const mergedValue = isDraftInvoiceQuickBooksTaxEnabled
          ? mergeDraftTaxCalculationResult(updatedQuoteItemValue, _data)
          : {
            ...updatedQuoteItemValue,
            discount: _data.discount,
            discountAmount: _data.discountAmount,
            totalPayment: _data.totalPayment,
            totalPrice: _data.totalPrice,
            totalPriceAfterDiscount: _data.totalPriceAfterDiscount,
            totalVAT: _data.totalVAT,
            vat: _data.vat,
          };
        mergedValue.exchangeRate =
          documentItems[0]?.exchangeRate === 0
            ? 1
            : documentItems[0]?.exchangeRate;
        if (isDraftInvoiceQuickBooksTaxEnabled) {
          applyDraftInvoiceDocumentState(mergedValue);
        } else {
          setQuoteItemValue(mergedValue);
        }
      }
    } else {
      startOptimisticTaxCalculation();
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessDelete();
          onCloseDeleteItemModal();
          res?.data?.succ === false ? navigate("/home") : getQuote();
        } else {
          alertFaultDelete();
        }
      };
      await deleteDocumentItemApi(callApi, callBack, {
        ItemId: quoteItemId,
        documentType: docType,
      });
    }
  };

  const onAddDelivery = async (shipmentType: any, deliveryPrice?) => {
    startOptimisticTaxCalculation();
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessAdded();
        onCloseDeliveryModal();
        onCloseUpdateDeliveryModal();
        if (
          router?.query?.isNewCreation &&
          (docType === DOCUMENT_TYPE.deliveryNote ||
            docType === DOCUMENT_TYPE.deliveryNoteRefund ||
            docType === DOCUMENT_TYPE.invoice ||
            docType === DOCUMENT_TYPE.invoiceRefund)
        ) {
          const updatedQuoteItemValue = { ...quoteItemValue };
          const deliveryItem = res?.data;
          const newItem = {
            id: uuidv4(),
            code: deliveryItem?.code,
            productName: deliveryItem.productName,
            details: deliveryItem?.details,
            price: deliveryItem.price,
            workName: "",
            discount: 0,
            finalPrice: 0,
            quantity: 1,
            isSelected: false,
            productID: deliveryItem?.productID,
            productType: 1,
            calculationStatus: null,
          };
          updatedQuoteItemValue.documentItems = [
            ...updatedQuoteItemValue.documentItems,
            newItem,
          ];
          getCalculateQuoteItem(
            newItem,
            1,
            newItem.price,
            updatedQuoteItemValue,
          );
        } else getQuote();
      } else {
        alertFaultAdded();
      }
    };
    await addDeliveryApi(callApi, callBack, {
      delivery: {
        documentId: quoteItemValue?.id,
        shipmentTypeId: shipmentType?.value,
        deliveryPrice,
      },
      documentType: docType,
    });
  };

  const onClickDeleteQouteItem = (quoteItem) => {
    onOpenDeleteItemModal();
    setQuateItemId(quoteItem?.id);
    setQuoteItem(quoteItem);
  };

  const isDeleteItemAffectsPackaging = useMemo(
    () =>
      shouldWarnPackagingOnItemDelete(
        quoteItem,
        quoteItemValue?.documentItems,
      ),
    [quoteItem, quoteItemValue?.documentItems],
  );

  const [, setLoading] = useRecoilState(loadingWithShadowState);

  const getCalculateQuote = async (
    calculationType: number,
    data: number,
    manualDestinationTaxes?: Array<{ jurisdictionKey: string; taxAmount: number }>,
  ) => {
    if (calculationType !== DocumentCalculationType.VAT) {
      startOptimisticTaxCalculation();
    }
    startDocumentPricesUpdateLock();
    try {
      if (isNewCreation) {
        const res = await callApi(
          EHttpMethod.POST,
          `/v1/erp-service/documents/calculate-document-new`,
          {
            documentType: docType,
            document: isDraftInvoiceQuickBooksTaxEnabled
              ? buildDraftCalculateDocumentPayload(quoteItemValue, {
                data,
                calculationType,
                discount: data,
              })
              : {
                exchangeRate:
                  quoteItemValue?.exchangeRate === 0
                    ? 1
                    : quoteItemValue?.exchangeRate,
                totalPrice: quoteItemValue?.totalPrice,
                data,
                calculationType,
                totalPriceAfterDiscount: quoteItemValue?.totalPriceAfterDiscount,
                discount: data,
                discountAmount: quoteItemValue?.discountAmount,
                totalPayment: quoteItemValue?.totalPayment,
                vat: quoteItemValue?.vat,
                totalVAT: quoteItemValue?.totalVAT,
                vatStatus: quoteItemValue?.vatStatus,
                roundPrices: quoteItemValue?.client?.roundPrices,
                documentItems: quoteItemValue.documentItems?.map((item) => ({
                  finalPrice: item.finalPrice,
                })),
              },
          },
          false,
        );
        if (res?.success) {
          const _data = res?.data?.data?.data;
          const updatedQuoteItemValue = isDraftInvoiceQuickBooksTaxEnabled
            ? mergeDraftTaxCalculationResult(quoteItemValue, _data)
            : {
              ...quoteItemValue,
              discount: _data.discount,
              discountAmount: _data.discountAmount,
              totalPayment: _data.totalPayment,
              totalPrice: _data.totalPrice,
              totalPriceAfterDiscount: _data.totalPriceAfterDiscount,
              totalVAT: _data.totalVAT,
              vat: _data.vat,
            };

          if (isDraftInvoiceQuickBooksTaxEnabled) {
            applyDraftInvoiceDocumentState(updatedQuoteItemValue);
          } else {
            setQuoteItemValue(updatedQuoteItemValue);
          }
        }
      } else {
        // Mark that footer table cells were updated (not via edit modal) for orders
        if (docType === DOCUMENT_TYPE.order) {
          setHasTableCellUpdates(true);
        }
        const callBack = (res) => {
          if (res?.success) {
            alertSuccessUpdate();
            getQuote();
          } else {
            alertFaultUpdate();
          }
        };
        await calculateDocumentApi(callApi, callBack, {
          documentType: docType,
          documentId: quoteItemValue?.id,
          data,
          calculationType,
          ...(manualDestinationTaxes?.length
            ? { manualDestinationTaxes: JSON.stringify(manualDestinationTaxes) }
            : {}),
        });
      }
    } finally {
      finishDocumentPricesUpdateLock();
    }
  };

  const revertUnsavedFinalPrice = (quoteItem: any) => {
    const itemId = quoteItem?.id;
    if (!itemId) {
      return;
    }

    const previousFinalPrice =
      quoteItem?.prevFinalPrice != null
        ? quoteItem.prevFinalPrice
        : quoteItem?.finalPrice;

    const revertItem = (item: any) => {
      if (item?.id !== itemId) {
        return item;
      }

      return {
        ...item,
        finalPrice: previousFinalPrice,
        prevFinalPrice: previousFinalPrice,
      };
    };

    setPriceListItems((prevItems) => {
      const updatedItems = (prevItems || []).map((item) => {
        const revertedItem = revertItem(item);
        if (!item?.childsDocumentItems) {
          return revertedItem;
        }

        return {
          ...revertedItem,
          childsDocumentItems: item.childsDocumentItems.map(revertItem),
        };
      });

      if (isQuoteConfirmation) {
        setQuoteConfirm((prev) => ({
          ...(prev || {}),
          documentItems: updatedItems,
        }));
      } else {
        setQuoteItemValue((prev) => ({
          ...(prev || {}),
          documentItems: updatedItems,
        }));
      }

      return updatedItems;
    });
  };

  const syncEditedDocumentItemsToSource = (updatedItems: any[]) => {
    setPriceListItems(updatedItems);

    // Keep the source state in sync with table edits, so later calculations
    // do not restore stale values from quoteItemValue/quoteConfirm.
    if (isQuoteConfirmation) {
      setQuoteConfirm((prev) => ({
        ...(prev || {}),
        documentItems: updatedItems,
      }));
    } else {
      setQuoteItemValue((prev) => ({
        ...(prev || {}),
        documentItems: updatedItems,
      }));
    }
  };

  const changeDocumentItems = (
    index: number,
    filedName: string,
    value: any,
  ) => {
    let temp = [...documentItems];
    const currentItem = temp[index];
    if (filedName === "finalPrice") {
      const isInTotalEditSession =
        currentItem.prevFinalPrice != null &&
        Number(currentItem.prevFinalPrice) !== Number(currentItem.finalPrice);

      temp[index] = {
        ...currentItem,
        finalPrice: value,
        prevFinalPrice: isInTotalEditSession
          ? currentItem.prevFinalPrice
          : currentItem.finalPrice,
      };
    } else if (
      filedName === "quantity" ||
      filedName === "price" ||
      filedName === "discount"
    ) {
      temp[index] = {
        ...currentItem,
        [filedName]: value,
        prevFinalPrice: currentItem.finalPrice,
      };
    } else {
      temp[index] = {
        ...temp[index],
        [filedName]: value,
      };
    }
    syncEditedDocumentItemsToSource(temp);
  };

  const changeDocumentItemsChild = (
    parentIndex: number,
    childInex: number,
    filedName: string,
    value: any,
  ) => {
    let temp = lodashClonedeep(documentItems);
    const currentItem = temp[parentIndex].childsDocumentItems[childInex];
    if (filedName === "finalPrice") {
      const isInTotalEditSession =
        currentItem.prevFinalPrice != null &&
        Number(currentItem.prevFinalPrice) !== Number(currentItem.finalPrice);

      temp[parentIndex].childsDocumentItems[childInex] = {
        ...currentItem,
        finalPrice: value,
        prevFinalPrice: isInTotalEditSession
          ? currentItem.prevFinalPrice
          : currentItem.finalPrice,
      };
    } else if (
      filedName === "quantity" ||
      filedName === "price" ||
      filedName === "discount"
    ) {
      temp[parentIndex].childsDocumentItems[childInex] = {
        ...currentItem,
        [filedName]: value,
        prevFinalPrice: currentItem.finalPrice,
      };
    } else {
      temp[parentIndex].childsDocumentItems[childInex] = {
        ...temp[parentIndex].childsDocumentItems[childInex],
        [filedName]: value,
      };
    }
    syncEditedDocumentItemsToSource(temp);
  };

  const changeQuoteItems = useCallback(
    (filedName: string, value: any) => {
      setQuoteItems((prev) => {
        return {
          ...prev,
          [filedName]: value,
        };
      });
    },
    [quoteItems],
  );

  const creationDateRef = useRef(null);
  const dateRef = useRef(null);
  const taxDateRef = useRef(null);
  const [activeClickAway, setActiveClickAway] = useState(false); //for duaDate/deliveryDate
  const [activeClickAwayForCreationDate, setActiveClickAwayForCreationDate] =
    useState(false);
  const [activeClickAwayForTaxDate, setActiveClickAwayForTaxDate] =
    useState(false);

  const [openOtherReasonModal, setOpenOtherReasonModal] = useState(false);
  const [openSignatureApprovalModal, setOpenSignatureApprovalModal] =
    useState(false);
  const [openIrrelevantCancelModal, setOpenIrrelevantCancelModal] =
    useState(false);
  const [openPriceCancelModal, setOpenPriceCancelModal] = useState(false);
  const [openDeliveryTimeCancelModal, setOpenDeliveryTimeCancelModal] =
    useState(false);
  const [openDuplicateCancelModal, setOpenDuplicateCancelModal] =
    useState(false);

  const onClickOpenIrrelevantModal = () => {
    setOpenIrrelevantCancelModal(true);
  };
  const onClickCloseIrrelevantModal = () => {
    setOpenIrrelevantCancelModal(false);
  };

  const onClickOpenPriceModal = () => {
    setOpenPriceCancelModal(true);
  };
  const onClickClosePriceModal = () => {
    setOpenPriceCancelModal(false);
  };

  const onClickOpenDeliveryTimeModal = () => {
    setOpenDeliveryTimeCancelModal(true);
  };
  const onClickCloseDeliveryTimeModal = () => {
    setOpenDeliveryTimeCancelModal(false);
  };
  const onClickOpenDuplicateModal = () => {
    setOpenDuplicateCancelModal(true);
  };
  const onClickCloseDuplicateModal = () => {
    setOpenDuplicateCancelModal(false);
  };
  const onClickOpenModal = () => {
    setOpenOtherReasonModal(true);
  };
  const onClickCloseModal = () => {
    setOpenOtherReasonModal(false);
  };

  const onClickOpenSignatureApprovalModal = () => {
    setOpenSignatureApprovalModal(true);
  };
  const onClickCloseSignatureApprovalModal = () => {
    setOpenSignatureApprovalModal(false);
  };

  const onClickCancelOffer = async () => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        navigate("/home");
      } else {
        alertFaultUpdate();
      }
    };
    await cancelDocumentApi(callApi, callBack, {
      DocumentType: docType,
      Document: {
        documentId: quoteItemValue?.id,
        quoteStatus: QuoteStatuses.CANCELED_OTHER,
        cancelText: reasonText,
      },
    });
  };

  const updateCancelQuote = async (quoteStatus: number) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        navigate("/home");
      } else {
        alertFaultUpdate();
      }
    };
    await cancelDocumentApi(callApi, callBack, {
      DocumentType: docType,
      Document: {
        documentId: quoteItemValue?.id,
        quoteStatus: quoteStatus,
      },
    });
  };

  const alertNotVerifiedMail = useModal();
  const [alertNotVerifiedModalState, setAlertModalState] = useState<{
    message: string;
    onConfirm?: () => void;
  }>({
    message: "",
  });

  const showAlertModal = ({
    message,
    onConfirm,
  }: {
    message: string;
    onConfirm?: () => void;
  }) => {
    setAlertModalState({ message, onConfirm });
    alertNotVerifiedMail.open();
  };

  // New handler for email - opens FrontDesk composer
  const onClickSendEmailViaComposer = async () => {
    if (!isSelectedAtLeastOne && docType === DOCUMENT_TYPE.quote) {
      alertFault("reports.pleaseSelectOneItem");
      return;
    }

    const contacts = quoteItemValue?.documentContacts;
    const emailValid = checkArrayNotEmptyOrEmailNotEmpty(contacts);

    if (!emailValid) {
      alertFault("sales.quote.mailContectErrorMsg");
      return;
    }

    // Get first valid email from contacts
    const contactEmail = contacts?.find((c) => c.contactMail)?.contactMail;
    const contactPhone = contacts?.find((c) => c.contactPhone)?.contactPhone;

    await openEmailComposer(quoteItemValue?.id, contactEmail, contactPhone);
  };

  const onClickSendQuoteToClient = async (messageType: MessageType) => {
    if (!isSelectedAtLeastOne && docType === DOCUMENT_TYPE.quote) {
      alertFault("reports.pleaseSelectOneItem");
      return;
    }

    const checkContacts = () => {
      const contacts = quoteItemValue?.documentContacts;
      const phoneValid = checkArrayNotEmptyOrPhoneNotEmpty(contacts);
      const emailValid = checkArrayNotEmptyOrEmailNotEmpty(contacts);

      if (messageType === MessageType.SMS && !phoneValid) {
        alertFault("sales.quote.phoneContectErrorMsg");
        return false;
      }
      // @ts-ignore
      if (messageType === MessageType.Email && !emailValid) {
        alertFault("sales.quote.mailContectErrorMsg");
        return false;
      }
      if (messageType === MessageType.Both) {
        if (!phoneValid) {
          alertFault("sales.quote.phoneContectErrorMsg");
          return false;
        }
        if (!emailValid) {
          alertFault("sales.quote.mailContectErrorMsg");
          return false;
        }
      }
      return true;
    };

    if (!checkContacts()) return;

    const callBack = (res) => {
      if (res?.success) {
        alertSuccessSend();
        setOpenInvoiceCreationModal(false);
      } else {
        // @ts-ignore
        if (
          (messageType === MessageType.Email ||
            messageType === MessageType.Both) &&
          res?.errors?.message
        ) {
          if (res.errors.message === "agent_not_verified") {
            showAlertModal({
              message: t("alerts.agentEmailNotVerified"),
              onConfirm: async () => {
                await resendFromPrintHouseMail(messageType);
              },
            });
          } else if (res.errors.message === "printHouse_mail_not_verified") {
            showAlertModal({
              message: t("alerts.printHouseMailNotVerified"),
            });
          } else if (res.errors.message === "app_password_required") {
            showAlertModal({
              message: t("alerts.app_password_required"),
            });
          } else {
            alertFaultSend();
          }
        }
      }
    };

    await sendDocumentToClientApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
        messageType,
      },
    });
  };

  const onClickCopyQuoteConfirmationLink = async () => {
    const quoteId = quoteItemValue?.id;
    if (!quoteId) {
      alertFault("alerts.linkCopyFailed");
      return;
    }

    const domain = printHouseProfileState?.domain;
    if (!domain) {
      alertFault("alerts.linkCopyFailed");
      return;
    }

    const host = domain.replace(/^https?:\/\//, "").replace(/\/$/, "");
    // Same format as QuotesService QuoteLink: https://{domain}/quote-confirmation?Id={quote.ID}
    const quoteConfirmationLink = `https://${host}/quote-confirmation?Id=${quoteId}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(quoteConfirmationLink);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = quoteConfirmationLink;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (!copied) {
          throw new Error("Copy command failed");
        }
      }
      alertSuccess("alerts.quoteConfirmationLinkCopied");
    } catch {
      alertFault("alerts.linkCopyFailed");
    }
  };

  const resendFromPrintHouseMail = async (messageType: MessageType) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessSend();
        setOpenInvoiceCreationModal(false);
      } else {
        if (res?.errors?.message === "printHouse_mail_not_verified") {
          showAlertModal({
            message: t("alerts.printHouseMailNotVerified"),
          });
        } else {
          alertFaultSend();
        }
      }
    };

    await sendDocumentToClientApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
        messageType: messageType,
        sendMailIfNotVerifiedAgent: true,
      },
    });
  };

  const receiptSendModal = useModal();
  const [modalCallback, setModalCallback] = useState<
    (includeInvoices: boolean) => void
  >(() => () => { });

  const openReceiptModal = (callback: (includeInvoices: boolean) => void) => {
    receiptSendModal.open();
    setModalCallback(() => callback);
  };

  const hasFrontDeskFeature = useRecoilValue(printHouseHasFrontDeskFeature);
  const frontDeskSend = useFrontDeskDocumentSend({
    documentType: docType,
    document: quoteItemValue,
    enabled: hasFrontDeskFeature,
    validate: () => {
      if (!isSelectedAtLeastOne && docType === DOCUMENT_TYPE.quote) {
        alertFault("reports.pleaseSelectOneItem");
        return false;
      }
      return true;
    },
    onLinked: (conversationId) =>
      setQuoteItemValue((prev) => (prev ? { ...prev, ticketId: conversationId } : prev)),
  });
  // Handler for frontdesk case: Send SMS and open email composer
  const handleOrderUpdateWithFrontDesk = useCallback(async () => {
    if (!quoteItemValue?.id) return;

    const firstContact = quoteItemValue?.documentContacts?.[0];
    const contactEmail =
      firstContact?.contactMail ||
      firstContact?.email ||
      quoteItemValue?.contactEmail ||
      quoteItemValue?.contactMail;
    const contactPhone =
      firstContact?.contactPhone ||
      firstContact?.phone ||
      quoteItemValue?.contactPhone;
    const contactName =
      firstContact?.contactName ||
      firstContact?.name ||
      quoteItemValue?.contactName;
    const customerId =
      quoteItemValue?.customerID ||
      quoteItemValue?.clientId ||
      quoteItemValue?.client?.id;
    const customerName =
      quoteItemValue?.customerName ||
      quoteItemValue?.clientName ||
      quoteItemValue?.client?.name;

    // Send SMS first
    const callBack = (res) => {
      if (res?.success) {
        // After SMS is sent, open email composer with documentType 20
        openEmailComposer(
          quoteItemValue.id,
          contactEmail,
          contactPhone,
          MessageType.Email, // messageType
          true, // sendMailIfNotVerifiedAgent
          false, // sendWithInvoices
          customerId,
          customerName,
          contactName,
          undefined, // managerEmails
          false, // isFromPaymentPage
          undefined, // paymentRequestId
          true, // isOrderUpdated
        );
      } else {
        alertFaultSend();
      }
    };

    await sendDocumentToClientApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
        messageType: MessageType.SMS, // Send SMS only
      },
    });
  }, [quoteItemValue, openEmailComposer, callApi, docType, alertFaultSend]);

  // Populate sendPaymentState with document data for order update
  const populateOrderUpdatePaymentState = useCallback(async () => {
    if (!quoteItemValue?.id) return;

    const callBack = (res) => {
      if (res?.success) {
        setSendPayment((prevState) => {
          const newSendPaymentState = { ...prevState };
          newSendPaymentState.documentId = quoteItemValue?.id;
          newSendPaymentState.documentContacts = res.data || [];

          // Set selectedContactIndex and populate contact fields
          if (res.data && res.data.length > 0) {
            const firstContact = res.data[0];
            newSendPaymentState.selectedContactIndex = 0;
            newSendPaymentState.contactName = (
              firstContact.contactName || ""
            ).trim();
            newSendPaymentState.contactPhone = (
              firstContact.contactPhone || ""
            ).trim();
            newSendPaymentState.contactMail = (
              firstContact.contactMail || ""
            ).trim();
          } else {
            // Fallback to document-level contact info if no contacts
            newSendPaymentState.selectedContactIndex = -1;
            const firstContact = quoteItemValue?.documentContacts?.[0];
            newSendPaymentState.contactName = (
              firstContact?.contactName ||
              quoteItemValue?.contactName ||
              ""
            ).trim();
            newSendPaymentState.contactPhone = (
              firstContact?.contactPhone ||
              quoteItemValue?.contactPhone ||
              ""
            ).trim();
            newSendPaymentState.contactMail = (
              firstContact?.contactMail ||
              quoteItemValue?.contactEmail ||
              quoteItemValue?.contactMail ||
              ""
            ).trim();
          }

          // Set default checkboxes (all checked by default)
          newSendPaymentState.sendBySms = true;
          newSendPaymentState.sendByMail = true;
          newSendPaymentState.sendByWhatsapp = false;

          return newSendPaymentState;
        });
      }
    };

    await getDocumentContactApi(callApi, callBack, {
      documentId: quoteItemValue?.id,
      documentType: docType,
    });
  }, [quoteItemValue, callApi, docType, setSendPayment]);

  // Handler for order update confirmation - Yes
  const handleOrderUpdateYes = useCallback(async () => {
    orderUpdateConfirmationModal.close();
    if (hasFrontDeskFeature) {
      // Frontdesk case: Send SMS and open email composer
      setIsOrderUpdateEmailComposer(true);
      handleOrderUpdateWithFrontDesk();
    } else {
      // Non-frontdesk case: Populate state and show SendPaymentModal
      await populateOrderUpdatePaymentState();
      orderUpdateSendModal.open();
    }
  }, [
    hasFrontDeskFeature,
    orderUpdateConfirmationModal,
    orderUpdateSendModal,
    handleOrderUpdateWithFrontDesk,
    populateOrderUpdatePaymentState,
  ]);

  // Handler for order update confirmation - No
  const handleOrderUpdateNo = useCallback(() => {
    orderUpdateConfirmationModal.close();
    // Clear the table cell update flag
    setHasTableCellUpdates(false);
    // Remove the URL parameter
    if (router.query.showUpdateModal) {
      const newQuery = { ...router.query };
      delete newQuery.showUpdateModal;
      router.replace(
        { pathname: router.pathname, query: newQuery },
        undefined,
        { shallow: true },
      );
    }
    //  navigate("/home");
  }, [orderUpdateConfirmationModal, router]);

  // Handler to navigate to home and clean up order update state
  const handleOrderUpdateComplete = useCallback(() => {
    // Clear the table cell update flag
    setHasTableCellUpdates(false);
    // Remove the URL parameter
    if (router.query.showUpdateModal) {
      const newQuery = { ...router.query };
      delete newQuery.showUpdateModal;
      router.replace(
        { pathname: router.pathname, query: newQuery },
        undefined,
        { shallow: true },
      );
    }
    // Navigate to home
    navigate("/home");
    // Reset order update email composer flag
    setIsOrderUpdateEmailComposer(false);
  }, [router, navigate]);

  const onClickSendReceiptToClient = async (
    messageType: number,
    sendWithInvoices: boolean = false,
  ) => {
    if (messageType === MessageType.SMS) {
      let checkPhones = checkArrayNotEmptyOrPhoneNotEmpty(
        quoteItemValue?.documentContacts,
      );
      if (checkPhones) {
        const callBack = (res) => {
          if (res?.success) {
            alertSuccessAdded();
            setOpenReceiptCreationModal(false);
          } else {
            alertFaultAdded();
          }
        };
        await sendReceiptToClientApi(callApi, callBack, {
          document: {
            documentId: quoteItemValue?.id,
            messageType,
            sendWithInvoices,
          },
        });
      } else {
        alertFault("sales.quote.phoneContectErrorMsg");
      }
    } else {
      let checkEmails = checkArrayNotEmptyOrEmailNotEmpty(
        quoteItemValue?.documentContacts,
      );
      if (checkEmails) {
        const callBack = (res) => {
          if (res?.success) {
            alertSuccessAdded();
            setOpenReceiptCreationModal(false);
          } else {
            alertFaultAdded();
          }
        };
        await sendReceiptToClientApi(callApi, callBack, {
          document: {
            documentId: quoteItemValue?.id,
            messageType,
            sendWithInvoices,
          },
        });
      } else {
        alertFault("sales.quote.mailContectErrorMsg");
      }
    }
  };

  const handleSaveDraftBtnClick = async () => {
    const currentItemsCount = quoteItemValue?.documentItems?.length || 0;
    /*if (currentItemsCount > 100) {
      alertWarning("sales.quote.maxItemsReached");
      return;
    }*/

    const callBack = (res) => {
      if (res?.success) {
        const path = isInvoice ? `/invoices` : `/purchaseInvoices`;
        navigate(path);
      } else {
        alertFaultUpdate();
      }
    };
    await saveDraftDocumentApi(callApi, callBack, {
      documentType: docType,
      document: {
        ...quoteItemValue,
        creationDate: creationDate,
        taxDate: taxDate,
        dueDate: selectDate,
        ...(router?.query?.budgetId ? { budgetID: router.query.budgetId } : {}),
        exchangeRate:
          quoteItemValue.exchangeRate === 0 ? 1 : quoteItemValue.exchangeRate,
        isOpenRelatedDocuments: isOpenRelatedDocuments,
        documentItems: getDocumentItemsForCreatePayload(),
      },
    });
  };

  const saveCurrentQuote = () =>
    saveDocumentApi(callApi, () => undefined, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
      },
    });

  const handleSaveBtnClick = async () => {
    const res = await saveCurrentQuote();
    if (res?.success) {
      // Show modal if table cells were updated (not via edit modal) and it's an order
      if (
        hasTableCellUpdates &&
        docType === DOCUMENT_TYPE.order &&
        quoteItemValue?.id &&
        !orderUpdateConfirmationModal.isOpen
      ) {
        orderUpdateConfirmationModal.open();
        // Clear the flag after showing modal
        setHasTableCellUpdates(false);
      } else if (
        router.query.showUpdateModal === "true" &&
        docType === DOCUMENT_TYPE.order &&
        quoteItemValue?.id &&
        !orderUpdateConfirmationModal.isOpen
      ) {
        orderUpdateConfirmationModal.open();
        setHasTableCellUpdates(false);
      } else {
        // Clear the flag if navigating away
        setHasTableCellUpdates(false);
        navigate("/home");
      }
    } else {
      alertFaultUpdate();
    }
  };

  const finishManagerQuoteDecision = async (
    outcome: "approved" | "rejected",
  ) => {
    const saved = await saveCurrentQuote();
    if (!saved?.success) {
      alertFaultUpdate();
      return;
    }
    await returnToQuoteApprovalConversation(outcome);
  };

  const handleRejectBtnClick = async () => {
    const res = await rejectDocumentApi(callApi, () => undefined, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
      },
    });
    if (res?.success) {
      await finishManagerQuoteDecision("rejected");
    } else {
      alertFaultUpdate();
    }
  };

  const handleAcceptBtnClick = async () => {
    const res = await acceptDocumentApi(callApi, () => undefined, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
      },
    });
    if (res?.success) {
      await finishManagerQuoteDecision("approved");
    } else {
      alertFaultUpdate();
    }
  };

  const returnToQuoteApprovalConversation = async (
    outcome: "approved" | "rejected",
  ) => {
    const quoteId = String(quoteItemValue?.id || "");
    const quoteNumber = String(
      quoteItemValue?.number || quoteItemValue?.documentNumber || "N/A",
    );

    let conversationId: string | null = null;
    try {
      if (quoteId) {
        const res = await directMessagesService.listConversations({
          limit: 100,
        });
        const matches = (res.conversations || []).filter((c) => {
          if (
            c.contextType === "QUOTE" &&
            String(c.contextId || "") === quoteId
          ) {
            return true;
          }
          const fromName = extractQuoteApprovalNumberFromTitle(c.name);
          return (
            Boolean(fromName) &&
            String(fromName).toLowerCase() === String(quoteNumber).toLowerCase()
          );
        });
        matches.sort((a, b) => {
          const aTime = new Date(a.lastMessageAt || 0).getTime();
          const bTime = new Date(b.lastMessageAt || 0).getTime();
          return bTime - aTime;
        });
        conversationId = matches[0]?.id || null;
      }

      if (conversationId) {
        const resultMessage = buildQuoteApprovalResultMessage({
          outcome,
          quoteNumber,
        });
        await directMessagesService
          .sendMessage(conversationId, {
            content: resultMessage.content,
            metadata: resultMessage.metadata,
          })
          .catch(() => undefined);

        setInternalChatConversationId(conversationId);
        setInternalChatMinimized(false);
        setInternalChatOpen(true);
        navigate(
          buildRoutePath(RouteBuilder.chatwootDirectMessages(conversationId)),
        );
        return;
      }
    } catch (err) {
      console.error(
        "[useQuote] Failed to return to quote approval conversation",
        err,
      );
    }

    navigate("/home");
  };

  const setLoadingWithShadow = useSetRecoilState(loadingWithShadowState);

  const onFileUploadStart = () => {
    setLoadingWithShadow(true);
  };

  const onFileUploadComplete = () => {
    setTimeout(() => {
      setLoadingWithShadow(false);
    }, 500);
  };

  /**
   * Link a document created from FrontDesk to its conversation (ERP TicketId).
   * Linking failures don't fail the save; the document can still be linked from FrontDesk.
   */
  const linkDocumentToFrontDeskConversation = async (documentId: string) => {
    const ticketId = chatwootTicketId?.trim();
    if (!ticketId || !isErpTicketDocumentType(docType)) return;

    try {
      const res = await assignToTicketApi(callApi, () => { }, {
        documentId,
        ticketId,
        documentType: docType,
      });
      if (!res?.success) {
        console.error("[linkDocumentToFrontDesk] ERP AssignToTicket failed");
      }
    } catch (error: any) {
      console.error("[linkDocumentToFrontDesk] Error linking document:", error?.message);
    }
  };

  const handleSaveBtnClickForDocument = async () => {
    // Check if we're creating a new document and if the document already has more than 100 items
    const isNewCreation = router?.query?.isNewCreation === "true";
    const currentItemCount = quoteItemValue?.documentItems?.length || 0;

    /*if (isNewCreation && currentItemCount > 100) {
      alertWarning("sales.quote.maxItemsReached");
      return;
    }*/

    // const validationError = validateDocument(quoteItemValue);
    // if (validationError) {
    //   alertFault(validationError);
    //   return;
    // }
    const callBack = async (res) => {
      if (res?.success) {
        alertSuccessAdded();
        const _data = res?.data || {};
        setQuoteItemValue(_data);

        const parseNumericValue = (value: unknown): number | null => {
          if (value === null || value === undefined || value === "") {
            return null;
          }
          if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
          }
          const normalizedValue = String(value).replace(/,/g, "").trim();
          const parsedValue = Number(normalizedValue);
          return Number.isFinite(parsedValue) ? parsedValue : null;
        };

        const isInvoiceOrPurchaseInvoice =
          docType === DOCUMENT_TYPE.invoice ||
          docType === DOCUMENT_TYPE.purchaseInvoice;
        let latestDocumentData = _data;
        const getTaxConfirmationValue = (payload: any): string => {
          const possibleValue =
            payload?.taxConfirmationNumber ??
            payload?.document?.taxConfirmationNumber ??
            payload?.documentData?.taxConfirmationNumber ??
            "";

          return possibleValue === null || possibleValue === undefined
            ? ""
            : String(possibleValue).trim();
        };

        if (isInvoiceOrPurchaseInvoice && _data?.id) {
          const getDocumentAfterCreate = (): Promise<any | null> =>
            new Promise(async (resolve) => {
              try {
                await getDocumentApi(
                  callApi,
                  (documentRes) => {
                    if (documentRes?.success) {
                      resolve(documentRes?.data || null);
                      return;
                    }
                    resolve(null);
                  },
                  {
                    // Backend endpoint commonly uses `Id` in query string.
                    Id: _data.id,
                    // Keep `id` for compatibility with existing backend handling.
                    id: _data.id,
                    documentType: docType,
                  },
                );
              } catch {
                resolve(null);
              }
            });

          const fetchedDocument = await getDocumentAfterCreate();
          if (fetchedDocument) {
            latestDocumentData = fetchedDocument;
            setQuoteItemValue(fetchedDocument);
          }
        }

        const taxThreshold = parseNumericValue(
          printHouseProfileState?.taxThreshold,
        );
        const totalBeforeVAT = parseNumericValue(
          latestDocumentData?.totalPrice ??
          latestDocumentData?.totalPriceWithoutVat,
        );
        const taxConfirmationNumber = getTaxConfirmationValue(latestDocumentData);
        const isTaxConfirmationMissing = !taxConfirmationNumber;

        if (
          isNewCreation &&
          isInvoiceOrPurchaseInvoice &&
          taxThreshold !== null &&
          totalBeforeVAT !== null &&
          totalBeforeVAT > taxThreshold &&
          isTaxConfirmationMissing
        ) {
          setOpenTaxConfirmationWarningModal(true);
        }

        if (_data?.id) {
          await linkDocumentToFrontDeskConversation(_data.id);
        }

        if (
          docType === DOCUMENT_TYPE.invoice ||
          docType === DOCUMENT_TYPE.deliveryNote
        ) {
          router.replace(`/${documentPath}?Id=${_data?.id}`);
          onClickOpenInvoiceCreation();
        } else {
          router.replace(`/${documentPath}?Id=${_data?.id}`);
        }
      } else {
        // Keep the cart as-is on failure so the user can correct and retry.
        const backendKey = res?.data?.message;
        const backendText = res?.data?.ErrorMessage;
        const stockGeneric = "An invoice cannot be created";
        if (backendText && backendText !== stockGeneric) {
          alertFaultRaw(backendText);
        } else if (backendKey) {
          alertFault(backendKey);
        } else {
          alertFaultAdded();
        }
      }
    };

    const createDocumentIdempotencyKey =
      shouldUseDocumentIdempotencyKey ? getDocumentIdempotencyKey() : undefined;

    await createDocumentApi(callApi, callBack, {
      documentType: docType,
      document: {
        ...quoteItemValue,
        ...(createDocumentIdempotencyKey
          ? { idempotencyKey: createDocumentIdempotencyKey }
          : {}),
        creationDate: creationDate,
        taxDate: taxDate,
        dueDate: selectDate,
        ...(router?.query?.budgetId ? { budgetID: router.query.budgetId } : {}),
        exchangeRate:
          quoteItemValue.exchangeRate === 0 ? 1 : quoteItemValue.exchangeRate,
        isOpenRelatedDocuments: isOpenRelatedDocuments,
        documentItems: getDocumentItemsForCreatePayload(),
        type: isMaterialPurchaseOrder
          ? PurchaseOrderType.Materials
          : PurchaseOrderType.Products,
      },
    });
  };

  const onClickDeleteAddress = async (item: any, addressType: EAddressType) => {
    const addressArray =
      addressType === EAddressType.BILLING
        ? "documentBillingAddresses"
        : "documentDeliveryAddresses";

    if (isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };

      // Remove the address with the matching ID
      updatedQuoteItemValue[addressArray] = updatedQuoteItemValue[
        addressArray
      ]?.filter((address) => address.id !== item.id);

      if (isDraftInvoiceQuickBooksTaxEnabled) {
        startOptimisticTaxCalculation();
        const calculatedData = await recalculateDraftInvoiceDocument(
          updatedQuoteItemValue,
        );
        if (calculatedData) {
          applyDraftInvoiceDocumentState(
            mergeDraftTaxCalculationResult(
              updatedQuoteItemValue,
              calculatedData,
            ),
          );
          return;
        }
      }

      setQuoteItemValue(updatedQuoteItemValue);
    } else {
      startOptimisticTaxCalculation();
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessDelete();
          getQuote();
        } else {
          alertFaultDelete();
        }
      };

      await deleteDocumentAddressApi(callApi, callBack, {
        documentAddressId: item?.id,
        documentType: docType,
      });
    }
  };

  const setCardTransactions = useSetRecoilState<transactionOptionsData[]>(
    creditTransactionsState,
  );
  const getAllCreditCardTransactions = async (id: string) => {
    const callBack = (res) => {
      if (res?.success) {
        const formattedData = res?.data.map((transaction) => ({
          value: transaction.id,
          label: transaction.text,
          transactionSum: transaction.transactionSum,
        }));
        setCardTransactions(formattedData);
      }
    };
    await getAllCreditTransactionsApi(callApi, callBack, { clientId: id });
  };

  useEffect(() => {
    getAllClientContacts();
  }, [getAllClientContacts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        if (activeClickAway) {
          updateDueDate();
          setActiveClickAway(false);
        }
      }

      if (taxDateRef.current && !taxDateRef.current.contains(event.target)) {
        if (activeClickAwayForTaxDate) {
          if (isNewCreation) {
            const updatedQuoteItemValue = { ...quoteItemValue };
            updatedQuoteItemValue.dueDate = selectDate;
            updatedQuoteItemValue.taxDate = taxDate;
            updatedQuoteItemValue.creationDate = creationDate;
            setQuoteItemValue(updatedQuoteItemValue);
          }
          setActiveClickAwayForTaxDate(false);
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [
    dateRef,
    taxDateRef,
    activeClickAway,
    activeClickAwayForTaxDate,
    quoteItemValue,
    selectDate,
    taxDate,
  ]);

  const [openReferenceModal, setOpenReferenceModal] = useState(false);
  const [confirmCreateDocument, setConfirmCreateDocument] = useState(false);
  const onClickOpenReferenceModal = () => {
    setOpenReferenceModal(true);
  };

  const onClickConfirmCreateDocument = () => {
    setConfirmCreateDocument(true);
  };

  const onClickCloseConfirmCreateDocument = () => {
    setConfirmCreateDocument(false);
  };

  const [newCreationDate, setNewCreationDate] = useState(
    isQuoteConfirmation
      ? quoteConfirm?.creationDate
      : quoteItemValue?.creationDate,
  );
  useEffect(() => {
    setNewCreationDate(
      isQuoteConfirmation
        ? quoteConfirm?.creationDate
        : quoteItemValue?.creationDate,
    );
  }, []);

  const onClickCloseReferenceModal = () => {
    setOpenReferenceModal(false);
  };
  const onClickCloseTaxConfirmationWarningModal = () => {
    setOpenTaxConfirmationWarningModal(false);
  };

  const onChangeDatesToCreationDate = () => {
    setSelectDate(newCreationDate);
    setCreationDate(newCreationDate);
    setTaxDate(newCreationDate);
    //make sure it works
    setQuoteItemValue({
      ...quoteItemValue,
      creationDate: newCreationDate,
      taxDate: newCreationDate,
      dueDate: newCreationDate,
    });
  };

  useEffect(() => {
    if (agentListValue?.length > 0) {
      const selectedAgent1 = agentListValue.find(
        (agent) => agent.value === quoteItemValue?.agentId,
      );
      setSelectedAgent(selectedAgent1);
    }
  }, [agentListValue, quoteItemValue]);

  useEffect(() => {
    if (customerSupportRepresentativeListValue?.length > 0) {
      const selected = customerSupportRepresentativeListValue.find(
        (employee) =>
          employee.value === quoteItemValue?.customerSupportRepresentativeId,
      );
      setSelectedCustomerSupportRepresentative(selected);
    }
  }, [customerSupportRepresentativeListValue, quoteItemValue]);

  useEffect(() => {
    const source = isQuoteConfirmation ? quoteConfirm : quoteItemValue;

    setPriceListItems(source?.documentItems || []);
    setQuoteItems(source);
    setItems(source?.documentContacts);
    setCreationDate(source?.creationDate);
    setTaxDate(source?.taxDate);
    setClientName(source?.occasionalClientName);

    // Check if we should skip setting the due date because we have valid checks
    const hasValidChecks = checksReceipt?.some(
      (check) => check.dueDate && check.checkSum > 0,
    );
    const shouldSkipDueDateUpdate =
      isReceipt && isOutgoingPayments && isNewCreation && hasValidChecks;

    // For quote confirmation, always use dueDate from get-document-by-id
    // For other documents, set dueDate if not a quote or if it's quote confirmation
    if ((!isQuote || isQuoteConfirmation) && !shouldSkipDueDateUpdate) {
      applyDeliveryDates({
        dueDate: source?.dueDate,
        userDueDate: source?.userDueDate,
      });
    }
  }, [
    quoteItemValue,
    quoteConfirm,
    isQuote,
    isQuoteConfirmation,
    checksReceipt,
    isReceipt,
    isOutgoingPayments,
    isNewCreation,
  ]);

  const isUseJobsTimeline = CheckPreferenceValue(
    EPreferences.USE_JOBS_TIMELINE,
  );
  const isDeliveryDateRequiredInQuote = CheckPreferenceValue(
    EPreferences.DELIVERY_DATE_REQUIRED_IN_QUOTE,
  );

  useEffect(() => {
    if (!isQuote) return;
    // For quote confirmation, only get estimated production date
    // Delivery date comes from get-document-by-id
    if (isQuoteConfirmation) {
      getEstimatedProductionDate();
      return;
    }
    getQuoteDeliveryDate();
  }, [isQuote, isQuoteConfirmation, quoteItemValue?.dueDateUpdatedByUser]);

  const prevRouterQueryRef = useRef(router.query);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!router.isReady || isQuoteConfirmation) {
      return;
    }

    if (isCreatingSalesInvoice && (!permissionsReady || !canCreateInvoice)) {
      return;
    }

    if (routerChangedFromClientSelect) {
      setRouterChangedFromClientSelect(false);
      return;
    }

    // Writing the create-document idempotency key into the URL must not
    // reload the draft. That second get-new-document-data call is what
    // showed "Getting data failed" after closing an order.
    const previousDocumentQuery = {
      ...(prevRouterQueryRef.current ?? {}),
    };
    const currentDocumentQuery = { ...(router.query ?? {}) };
    delete previousDocumentQuery.idempotencyKey;
    delete currentDocumentQuery.idempotencyKey;
    const hasQueryChanged =
      JSON.stringify(previousDocumentQuery) !==
      JSON.stringify(currentDocumentQuery);

    if (!hasMountedRef.current || hasQueryChanged) {
      const sourceChanged =
        hasMountedRef.current &&
        (prevRouterQueryRef.current?.orderId !== router.query.orderId ||
          prevRouterQueryRef.current?.deliveryNoteId !==
            router.query.deliveryNoteId ||
          prevRouterQueryRef.current?.documentId !== router.query.documentId ||
          prevRouterQueryRef.current?.isNewCreation !==
            router.query.isNewCreation);

      if (sourceChanged) {
        setQuoteItemValue({});
      }

      getQuote();
      getAllEmployees();
      getCustomerSupportRepresentatives();
      hasMountedRef.current = true;
      prevRouterQueryRef.current = router.query;
    }
  }, [
    router.isReady,
    isQuoteConfirmation,
    router.query,
    isCreatingSalesInvoice,
    permissionsReady,
    canCreateInvoice,
  ]);

  useEffect(() => {
    if (!router.isReady || !isCreatingSalesInvoice || !permissionsReady) {
      return;
    }
    if (!canCreateInvoice) {
      navigate("/home");
    }
  }, [
    router.isReady,
    isCreatingSalesInvoice,
    permissionsReady,
    canCreateInvoice,
    navigate,
  ]);

  // useEffect(() => {
  //   if (!isQuoteConfirmation) {
  //     getQuote();
  //     getAllEmployees();
  //   }
  // }, [isQuoteConfirmation]);

  const { documentTitle } = useDocumentTitle(docType, isOutgoingPayments);

  const [openLoginModal, setOpenLoginModal] = useState<boolean>(false);

  const onClickClosLoginModal = () => {
    setOpenLoginModal(false);
  };

  const onClickOpenLoginModal = () => {
    setOpenLoginModal(true);
  };

  const { getClientTypesCategories } = useClientTypesList();

  useEffect(() => {
    if (!isPrePaymentPage) getClientTypesCategories();
  }, []);

  const sortDocumentItems = async (sortType: number) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getQuote();
      } else {
        alertFaultUpdate();
      }
    };
    await sortDocumentItemsApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
        sortType,
        sortLayers: null,
      },
    });
  };
  const sortLayersRequestRef = useRef(0);
  const persistQuoteItemSortLayers = useCallback(
    async (rules: QuoteItemSortRule[]) => {
      const documentId = quoteItemValue?.id;
      if (!documentId || docType !== DOCUMENT_TYPE.quote) {
        return;
      }

      const requestId = ++sortLayersRequestRef.current;
      const sortLayers = toPersistedSortLayers(rules);
      const callBack = (res) => {
        if (requestId !== sortLayersRequestRef.current) {
          return;
        }
        if (res?.success) {
          setQuoteItemValue((prev) =>
            prev?.id === documentId ? { ...prev, sortLayers } : prev,
          );
          if (sortLayers.length === 0) {
            getQuote();
          }
          return;
        }
        alertFaultUpdate();
      };
      await sortDocumentItemsApi(
        callApi,
        callBack,
        {
          documentType: docType,
          document: {
            documentId,
            sortLayers,
          },
        },
        false,
      );
    },
    [
      quoteItemValue?.id,
      docType,
      callApi,
      alertFaultUpdate,
      setQuoteItemValue,
      getQuote,
    ],
  );
  const updateDiscountType = async (discountType: number) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getQuote();
      } else {
        alertFaultUpdate();
      }
    };
    await updateDiscountTypeApi(callApi, callBack, {
      documentType: docType,
      document: {
        doucumentId: quoteItemValue?.id,
        discountType,
      },
    });
  };

  const updateDocumentPreference = async (
    value: string,
    documentSettingTYpe: number,
  ) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getQuote();
      } else {
        alertFaultUpdate();
      }
    };
    await updateDocumentPreferenceApi(callApi, callBack, {
      documentType: docType,
      data: {
        value: value,
        documentSetting: documentSettingTYpe,
      },
    });
  };

  const updateHideDiscountColumn = async (hideDiscountColumn: boolean) => {
    const previousHideDiscountColumn = Boolean(
      quoteItemValue?.hideDiscountColumn
    );
    const documentId = quoteItemValue?.id;
    setQuoteItemValue((prev) => ({
      ...prev,
      hideDiscountColumn,
    }));

    if (!documentId) {
      return;
    }

    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        if (!isNewCreation) {
          getQuote();
        }
        return;
      }

      if (isNewCreation) {
        return;
      }

      setQuoteItemValue((prev) => ({
        ...prev,
        hideDiscountColumn: previousHideDiscountColumn,
      }));

      if (res?.errors?.message === "discount_is_greater_than_zero") {
        alertFault(t("alerts.discount_is_greater_than_zero"));
      } else {
        alertFaultUpdate();
      }
    };

    await updateHideDiscountColumnApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId,
        hideDiscountColumn,
      },
    });
  };

  const updatePaymentRequired = async (isPaymentRequired: boolean) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        if (res?.data) {
          setQuoteItemValue(res.data);
        } else {
          getQuote();
        }
      } else {
        alertFaultUpdate();
      }
    };
    await updatePaymentRequiredApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
      },
    });
  };

  const [whatsappMassage, setWhatsAppMassage] = useState("");

  const getWhatsAppMessage = async () => {
    if (quoteItemValue?.id) {
      const callBack = (res) => {
        if (res?.success) {
          setWhatsAppMassage(res?.data);
        } else {
          alertFaultUpdate();
        }
      };
      await getWhatsAppMessageApi(callApi, callBack, {
        documentId: quoteItemValue?.id,
        documentType: docType,
      });
    }
  };

  const [sendWithInvoices, setSendWithInvoices] = useState<boolean>(false);
  const getWhatsAppMessageForReceipt = async () => {
    if (quoteItemValue?.id) {
      const callBack = (res) => {
        if (res?.success) {
          setWhatsAppMassage(res?.data);
        } else {
          alertFaultUpdate();
        }
      };
      await getWhatsAppMessageForReceiptApi(callApi, callBack, {
        documentId: quoteItemValue?.id,
        sendWithInvoices,
      });
    }
  };

  const onChangeSelectedItemRowForQoute = async (
    isSelected: boolean,
    itemId: string,
  ) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getQuote();
      } else {
        alertFaultUpdate();
      }
    };
    await updateDocuementItemSelectApi(callApi, callBack, {
      documentType: docType,
      item: {
        documentItemId: itemId,
        isSelected,
      },
    });
  };

  const isShowSettingIcon =
    !(isNewCreation && (isDeliveryNote || isInvoice)) && quoteItemValue.client;

  const showVatListInSettingIconNew =
    isNewCreation && (isDeliveryNote || isInvoice) && quoteItemValue.client;

  const moveOrderToReady = async (isSendMessage: boolean = false) => {
    const callBack = (res) => {
      if (res?.success) {
        if (isSelectedCLoseAsDeliveryNote) {
          navigate(
            `/deliveryNote?isNewCreation=true&orderId=${selectedDocument?.id}`,
          );
        } else if (isSelectedCLoseAsProformaInvoice) {
          const path = isOrder
            ? `/proformaInvoice?isNewCreation=true&orderId=${selectedDocument?.id}`
            : `/proformaInvoice?isNewCreation=true&deliveryNoteId=${selectedDocument?.id}`;
          navigate(path);
        } else {
          const path = isOrder
            ? `/invoice?isNewCreation=true&orderId=${selectedDocument?.id}`
            : `/invoice?isNewCreation=true&deliveryNoteId=${selectedDocument?.id}`;
          navigate(path);
        }
      } else {
        alertFaultUpdate();
      }
    };
    await moveOrderToReadyApi(callApi, callBack, {
      orderId: selectedDocument?.id,
      isSendMessage: isSendMessage,
    });
  };

  const [allClientActiveBudgets, setAllClientActiveBudgets] = useState([]);
  const getAllClientActiveBudgets = async () => {
    const callBack = (res) => {
      if (res?.success) {
        setAllClientActiveBudgets(res.data);
      }
    };
    await getAllClientActiveBudgetsApi(callApi, callBack, {
      clientId: quoteItemValue?.client?.id,
    });
  };
  const [selectedBudget, setSelectedBudget] = useState(null);

  useEffect(() => {
    if (quoteItemValue?.client?.id) {
      getAllClientActiveBudgets();
    }
  }, [quoteItemValue?.client?.id]);

  const updateDocumentBudget = useCallback(
    async (budget) => {
      // Store the previous budget state for rollback
      const previousBudget = quoteItemValue?.budgetId
        ? allClientActiveBudgets.find((b) => b.id === quoteItemValue.budgetId)
        : null;

      const res = await callApi(
        EHttpMethod.PUT,
        `/v1/erp-service/budgets/update-document-budget`,
        {
          documentType: docType,
          documentId: quoteItemValue?.id,
          budgetId: budget.id,
        },
      );
      if (res?.success) {
        alertSuccessUpdate();
        getQuote();
      } else {
        alertFault(`
          ${t("sales.quote.errorUpdateBudget")}
        `);
        // Rollback to the previous budget state
        setSelectedBudget(previousBudget);
      }
    },
    [docType, quoteItemValue, allClientActiveBudgets],
  );

  // State for intake validation dialog
  const [showIntakeValidationDialog, setShowIntakeValidationDialog] =
    useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<number | null>(
    null,
  );

  const updatePurchaseOrderStatus = useCallback(
    async (status: number, shouldUndoIntake: boolean | null = null) => {
      if (!isPurchaseOrder || !quoteItemValue?.id) {
        return;
      }

      if (isPurchaseOrderStatusChangeLocked(quoteItemValue)) {
        return;
      }

      // Get current status - use status property (as per API response structure)
      const currentStatus =
        quoteItemValue?.status ?? quoteItemValue?.documentStatus;

      // Check if changing FROM Received status to another status
      // Only show dialog if shouldUndoIntake is null (not explicitly set from dialog handlers)
      if (
        shouldUndoIntake === null &&
        currentStatus === PurchaseOrderStatuses.Received &&
        status !== PurchaseOrderStatuses.Received
      ) {
        // Check if document has any items with isIntake = true
        const hasIntakedItems = quoteItemValue?.documentItems?.some(
          (item: any) => item.isIntake === true,
        );
        console.log("hasIntakedItems", hasIntakedItems);

        if (hasIntakedItems) {
          // Show dialog asking if user wants to undo intake
          setPendingStatusChangeWithUndo({ status, shouldUndoIntake: false });
          setShowUndoIntakeDialog(true);
          return;
        }
      }

      // Default shouldUndoIntake to false if not explicitly set
      const shouldUndo = shouldUndoIntake === null ? false : shouldUndoIntake;

      // Check if changing to Received status
      if (status === PurchaseOrderStatuses.Received) {
        const hasNotFullyIntakedItems = quoteItemValue?.documentItems?.some(
          (item: any) => item.isFullyIntaked !== true,
        );

        if (hasNotFullyIntakedItems) {
          // Show validation dialog
          setPendingStatusChange(status);
          setShowIntakeValidationDialog(true);
          return;
        }
      }

      // Proceed with status change
      setIsUpdatingPurchaseStatus(true);
      try {
        // If shouldUndo is true, call undo intake API (which also updates status)
        if (shouldUndo) {
          const undoResponse = await undoDocumentIntakeApi(callApi, () => { }, {
            documentType: docType,
            purchaseOrderId: quoteItemValue.id,
            status: status,
            shouldUndoIntake: true,
          });
          if (undoResponse?.success) {
            alertSuccessUpdate();
            await getQuote();
          } else {
            alertFaultUpdate();
          }
        } else {
          // If not undoing intake, just update status normally
          const response = await updateDocumentStatusApi(callApi, () => { }, {
            documentType: docType,
            documentId: quoteItemValue.id,
            status,
          });
          if (response?.success) {
            alertSuccessUpdate();
            await getQuote();
          } else {
            alertFaultUpdate();
          }
        }
      } catch (error) {
        alertFaultUpdate();
      } finally {
        setIsUpdatingPurchaseStatus(false);
      }
    },
    [
      alertFaultUpdate,
      alertSuccessUpdate,
      callApi,
      docType,
      getQuote,
      isPurchaseOrder,
      quoteItemValue?.id,
      quoteItemValue?.documentStatus,
      quoteItemValue?.documentItems,
    ],
  );

  // Handle undo intake dialog - yes (undo intake)
  const handleConfirmUndoIntake = () => {
    if (pendingStatusChangeWithUndo) {
      setShowUndoIntakeDialog(false);
      updatePurchaseOrderStatus(pendingStatusChangeWithUndo.status, true);
      setPendingStatusChangeWithUndo(null);
    }
  };

  // Handle undo intake dialog - no (don't undo intake)
  const handleCancelUndoIntake = () => {
    if (pendingStatusChangeWithUndo) {
      setShowUndoIntakeDialog(false);
      updatePurchaseOrderStatus(pendingStatusChangeWithUndo.status, false);
      setPendingStatusChangeWithUndo(null);
    }
  };

  // Handle intake validation dialog - open intake modal
  const handleOpenIntakeFromValidation = () => {
    setShowIntakeValidationDialog(false);
    onClickOpenInventoryIntakeModal();
  };

  // Handle intake validation dialog - cancel
  const handleCancelIntakeValidation = () => {
    setShowIntakeValidationDialog(false);
    setPendingStatusChange(null);
  };

  // State for undo intake dialog when changing from Received status
  const [showUndoIntakeDialog, setShowUndoIntakeDialog] = useState(false);
  const [pendingStatusChangeWithUndo, setPendingStatusChangeWithUndo] =
    useState<{ status: number; shouldUndoIntake: boolean } | null>(null);
  console.log("pendingStatusChangeWithUndo", pendingStatusChangeWithUndo);
  // Handle undo document intake
  const handleUndoDocumentIntake = useCallback(
    async (document?: any) => {
      if (!isPurchaseOrder || !quoteItemValue?.id) {
        return;
      }

      setIsUpdatingPurchaseStatus(true);
      try {
        const response = await undoDocumentIntakeApi(callApi, () => { }, {
          documentType: docType,
          purchaseOrderId: quoteItemValue.id,
          status: quoteItemValue.documentStatus || 0,
          shouldUndoIntake: true,
        });
        if (response?.success) {
          alertSuccessUpdate();
          await getQuote();
        } else {
          alertFaultUpdate();
        }
      } catch (error) {
        alertFaultUpdate();
      } finally {
        setIsUpdatingPurchaseStatus(false);
      }
    },
    [
      isPurchaseOrder,
      quoteItemValue?.id,
      quoteItemValue?.documentStatus,
      callApi,
      docType,
      getQuote,
      alertSuccessUpdate,
      alertFaultUpdate,
    ],
  );

  // Handle undo item intake
  const handleUndoItemIntake = useCallback(
    async (item: any) => {
      if (!isPurchaseOrder || !item?.id) {
        return;
      }

      try {
        const response = await undoDocumentItemIntakeApi(callApi, () => { }, {
          documentType: docType,
          itemId: item.id,
        });
        if (response?.success) {
          alertSuccessUpdate();
          await getQuote();
        } else {
          alertFaultUpdate();
        }
      } catch (error) {
        alertFaultUpdate();
      }
    },
    [
      isPurchaseOrder,
      callApi,
      docType,
      getQuote,
      alertSuccessUpdate,
      alertFaultUpdate,
    ],
  );

  const [relatedDocumentsModalOpen, setRelatedDocumentsModalOpen] =
    useState(false);
  const [relatedDocumentsData, setRelatedDocumentsData] =
    useState<unknown>(null);
  const [relatedDocumentsLoading, setRelatedDocumentsLoading] =
    useState(false);

  const handleCloseRelatedDocumentsModal = useCallback(() => {
    setRelatedDocumentsModalOpen(false);
    setRelatedDocumentsData(null);
  }, []);

  const handleShowRelatedDocuments = useCallback(
    async (item: any) => {
      const supportsItemRelatedDocuments =
        isPurchaseOrder || isDeliveryNote || isInvoice;
      if (!supportsItemRelatedDocuments || !item?.id) {
        return;
      }
      setRelatedDocumentsModalOpen(true);
      setRelatedDocumentsData(null);
      setRelatedDocumentsLoading(true);
      try {
        const response = await getDocumentItemRelatedDocumentsApi(
          callApi,
          () => { },
          { documentType: docType, itemId: item.id },
        );
        if (response?.success) {
          setRelatedDocumentsData(response.data);
        } else {
          alertFaultUpdate();
          setRelatedDocumentsModalOpen(false);
        }
      } catch {
        alertFaultUpdate();
        setRelatedDocumentsModalOpen(false);
      } finally {
        setRelatedDocumentsLoading(false);
      }
    },
    [isPurchaseOrder, isDeliveryNote, isInvoice, callApi, docType, alertFaultUpdate],
  );

  const handleSendUsingWhatsAppWeb = (sendWithInvoices = false) => {
    let checkPhones = checkArrayNotEmptyOrPhoneNotEmpty(
      quoteItemValue?.documentContacts,
    );
    if (!checkPhones) {
      alertFault("sales.quote.phoneContectErrorMsg");
      return;
    }
    const phoneNumber = quoteItemValue?.documentContacts[0].contactPhone;
    const callBack = (res) => {
      openWhatsAppWebLink(res?.data, phoneNumber);
    };
    getWhatsAppMessageApi(callApi, callBack, {
      documentId: quoteItemValue?.id,
      documentType: docType,
      ...(isReceipt ? { sendWithInvoices } : {}),
    });
  };

  const onClickDeleteDelivery = async () => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        notifyDocumentShippingInvalidated({
          documentId: quoteItemValue?.id,
          documentType: docType,
        });
        getQuote();
      } else {
        alertFaultUpdate();
      }
    };
    await deleteDeliveryItemApi(callApi, callBack, {
      documentId: quoteItemValue?.id,
      documentType: docType,
    });
  };

  const [selectedQuote, setSelectedQuote] = useState<any>();
  const [openChangeStatusModal, setOpenChangeStatusModal] = useState(false);

  const onClickOpenChangeStatusModal = (quote: any) => {
    setSelectedQuote(quote);
    setOpenChangeStatusModal(true);
  };

  const onClickCloseChangeStatusModal = () => {
    setOpenChangeStatusModal(false);
    setSelectedQuote(null);
  };

  const updateQuoteStatus = async (quote?) => {
    const callBack = (res) => {
      if (res?.success) {
        navigate("/quote");
      } else {
        if (res?.statusCode === 85) {
          alertFault(res?.message);
        } else {
          alertFaultUpdate();
        }
      }
    };
    await updateDocumentApi(callApi, callBack, {
      documentType: 0,
      document: {
        documentId: quote?.id ?? selectedQuote?.id,
      },
    });
  };

  const onclickCreateNew = async () => {
    const callBack = (res) => {
      if (res?.success) {
        const isAnotherQuoteInCreate = res?.data?.isAnotherQuoteInCreate;
        const documentId = res?.data?.documentId;
        if (!isAnotherQuoteInCreate) {
          navigate("/quote");
        } else {
          onClickOpenChangeStatusModal({ id: documentId });
        }
      } else {
        alertFaultUpdate();
      }
    };
    await createNewDocumentApi(callApi, callBack, { documentType: 0 });
  };

  const onOpenPurchaseOrderTypeModal = () => {
    setOpenPurchaseOrderTypeModal(true);
  };

  const onClosePurchaseOrderTypeModal = () => {
    setOpenPurchaseOrderTypeModal(false);
  };

  const handleSelectPurchaseOrderType = (isForMaterial: boolean) => {
    setOpenPurchaseOrderTypeModal(false);
    const url = isForMaterial
      ? `/${documentPath}?isNewCreation=true&isMaterial=true`
      : `/${documentPath}?isNewCreation=true`;
    navigate(url);
  };

  const renderCreateNewButton = () => {
    if (docType === DOCUMENT_TYPE.invoice && !canCreateInvoice) {
      return null;
    }

    return (
    <Button
      sx={{
        ...classes.createNewDocument,
        "& .MuiButton-startIcon": {
          ...(RTL
            ? { marginLeft: "4px", marginRight: 0 }
            : { marginRight: "4px", marginLeft: 0 }),
        },
      }}
      onClick={() => {
        if (isPurchaseOrder) {
          onOpenPurchaseOrderTypeModal();
        } else if (isOrder) {
          onclickCreateNew();
        } else {
          navigate(
            isOutgoingPayments
              ? `/${documentPath}?isOutgoingPayments=true&isNewCreation=true`
              : `/${documentPath}?isNewCreation=true`,
          );
        }
      }}
      startIcon={<PlusIcon stroke={primaryColor(600)} />}
    >
      {t("sales.quote.createNew")}
    </Button>
    );
  };

  const reCalculateItem = async (itemId: string) => {
    startOptimisticTaxCalculation();
    const callBack = (res) => {
      if (res?.success) {
        // alertSuccessUpdate();
      } else {
        alertFaultUpdate();
      }
    };
    await reCalculateItemApi(callApi, callBack, {
      documentType: docType,
      documentItemId: itemId,
    });
  };

  // Export to Excel handler
  const handleExportToExcel = () => {
    const {
      exportQuoteToExcel,
    } = require("@/widgets/quote-new/utils/export-quote-to-excel");
    exportQuoteToExcel({
      documentItems: quoteItemValue?.documentItems || [],
      documentType: docType,
      documentNumber: quoteItemValue?.number,
      clientName: quoteItemValue?.client?.name,
      t,
    });
  };

  return {
    dateRef,
    taxDateRef,
    creationDateRef,
    activeClickAway,
    selectDate,
    creationDate,
    taxDate,
    isUpdateBusinessName,
    isUpdatePurchaseNumber,
    isUpdateAddress,
    selectedAgent,
    agentListValue,
    isUpdateAgent,
    selectedCustomerSupportRepresentative,
    customerSupportRepresentativeListValue,
    isUpdateCustomerSupportRepresentative,
    isDisplayWidget,
    clientContactsValue,
    isUpdateContactName,
    items,
    displayedItems,
    isUpdateContactEmail,
    isUpdateContactMobile,
    selectedContactById,
    openDeleteModalContact,
    selectedContact,
    isUpdateContactName1,
    isUpdateContactEmail1,
    isUpdateContactMobile1,
    openAddNewItemModal,
    openDuplicateWithDifferentQTYModal,
    openDeleteItemModal,
    isDeleteItemAffectsPackaging,
    columnWidths,
    headerHeight,
    documentItems,
    quoteItems,
    tableHeaders,
    anchorElCancelBtn,
    anchorElSendBtn,
    openSendBtn,
    openCancelBtn,
    openOtherReasonModal,
    openIrrelevantCancelModal,
    openPriceCancelModal,
    openDeliveryTimeCancelModal,
    openDuplicateCancelModal,
    anchorElSettingMenu,
    openSettingMenu,
    anchorElSettingMenuForVat,
    openNewSttingMenuForVat,
    onClickSendQuoteToClient,
    onClickCopyQuoteConfirmationLink,
    onClickSendReceiptToClient,
    handleSettingMenuClick,
    handleSettingMenuClickForVat,
    handleSettingMenuClose,
    handleSettingMenuCloseForVat,
    onClickOpenPriceModal,
    onClickOpenModal,
    onClickClosePriceModal,
    onClickOpenDeliveryTimeModal,
    onClickCloseDeliveryTimeModal,
    onClickOpenDuplicateModal,
    onClickCloseDuplicateModal,
    onClickCloseModal,
    onClickOpenIrrelevantModal,
    onClickCloseIrrelevantModal,
    handleCancelBtnClick,
    handleCancelBtnClose,
    handleSendBtnClick,
    handleSendBtnClose,
    setActiveClickAway,
    changeQuoteItems,
    changeDocumentItemsChild,
    changeDocumentItems,
    getCalculateQuote,
    onCloseDeleteItemModal,
    deleteQuoteItem,
    onCloseDuplicateWithDifferentQTY,
    setAmountValue,
    onOpenNewItem,
    onCloseNewItem,
    setIsUpdateContactName1,
    setIsUpdateContactEmail1,
    setIsUpdateContactMobile1,
    setSelectDate,
    commitDeliveryDateChange,
    selectDeliveryDateSource,
    setTaxDate,
    setCreationDate,
    updateDueDate,
    setIsUpdateBusinessName,
    setIsUpdateAddress,
    setIsUpdatePurchaseNumber,
    setIsUpdateBusinessCode,
    onBlurBusinessName,
    onBlurPurchaseNumber,
    onBlurBusinessCode,
    onBlurAddress,
    onBlurAgent,
    setIsUpdateAgent,
    updateAgent,
    onBlurCustomerSupportRepresentative,
    setIsUpdateCustomerSupportRepresentative,
    updateCustomerSupportRepresentative,
    t,
    handleShowLess,
    setIsDisplayWidget,
    onOpenDeleteModalContact,
    changeItems,
    updateClientContact,
    setIsUpdateContactName,
    setIsUpdateContactMobile,
    setIsUpdateContactEmail,
    handleShowMore,
    setSelectedContactById,
    onInputChangePhone,
    onInputChangeMail,
    onClickAddNewContact,
    onCloseDeleteModalContact,
    onClickDeleteContact,
    onBlurContactMobile,
    onBlurContactName,
    onBlurContactEmail,
    getCalculateQuoteItem,
    getCalculateQuoteConfirmationItem,
    onClickDuplicateWithDifferentQTY,
    duplicateQuoteItemWithAnotherQuantity,
    onClickDeleteQouteItem,
    setReasonText,
    onClickCancelOffer,
    updateCancelQuote,
    onChangeSelectBusiness,
    onClickDeleteAddress,
    openAddDeliveryModal,
    onOpenDeliveryModal,
    onCloseDeliveryModal,
    openUpdateDeliveryModal,
    onOpenUpdateDeliveryModal,
    onCloseUpdateDeliveryModal,
    onAddDelivery,
    handleSaveBtnClick,
    handleRejectBtnClick,
    handleAcceptBtnClick,
    handleSaveBtnClickForDocument,
    documentTitle,
    onBlurExchangeRate,
    onBlurCurrency,
    setIsUpdateExchangeRate,
    setIsUpdateCurrency,
    setIsUpdateVatStatus,
    onBlurVatStatus,
    isUpdateVatStatus,
    isUpdateExchangeRate,
    isUpdateCurrency,
    updateExchangeRate,
    updateCurrency,
    updateVatStatus,
    updateDefaultLanguage,
    onBlurDefaultLanguage,
    isUpdateDefaultLanguage,
    setIsUpdateDefaultLanguage,
    refreshExchangeRate,
    getQuote,
    openCopyFromOrderModal,
    onCloseCopyFromOrder,
    onOpenCopyFromOrder,
    getAllClientContacts,
    copyFromDocumentType,
    openLoginModal,
    onClickClosLoginModal,
    onClickOpenLoginModal,
    openWhatsAppModal,
    onClickOpenWhatsAppModal,
    onClickCloseWhatsAppModal,
    sortDocumentItems,
    persistQuoteItemSortLayers,
    updateDocumentPreference,
    updateHideDiscountColumn,
    updatePaymentRequired,
    getWhatsAppMessage,
    getWhatsAppMessageForReceipt,
    whatsappMassage,
    openAddNewContactModal,
    onCloseNewContact,
    onOpenNewContact,
    onChangeSelectedItemRowForQoute,
    openNewItemNotesModal,
    onClickCloseNewItemNotesModal,
    onClickOpenRelatedDocumentsModal,
    onClickCloseRelatedDocumentsModal,
    openRelatedDocumentsModal,
    openSignatureApprovalModal,
    onClickOpenSignatureApprovalModal,
    onClickCloseSignatureApprovalModal,
    onBlurClientName,
    isUpdateClientName,
    setIsUpdateClientName,
    clientName,
    setClientName,
    onClickOpenNewItemNotesModal,
    setActiveClickAwayForCreationDate,
    setActiveClickAwayForTaxDate,
    openReferenceModal,
    onClickOpenReferenceModal,
    onClickCloseReferenceModal,
    onChangeDatesToCreationDate,
    activeClickAwayForCreationDate,
    newCreationDate,
    setNewCreationDate,
    openInvoiceCreationModal,
    onClickCloseInvoiceCreation,
    updateDiscountType,
    onBlurTitle,
    isUpdateDocumentTitle,
    setIsUpdateDocumentTitle,
    isShowSettingIcon,
    showVatListInSettingIconNew,
    isInvoice,
    onClickOpenChangeProductionStatusModal,
    openChangeProductionStatusModal,
    onClickCloseChangeProductionStatusModal,
    selectedDocument,
    isSelectedCLoseAsDeliveryNote,
    isSelectedCLoseAsProformaInvoice,
    navigate,
    moveOrderToReady,
    allClientActiveBudgets,
    updateDocumentBudget,
    selectedBudget,
    setSelectedBudget,
    onCloseQuickDuplicate,
    onClickQuickDuplicate,
    quoteItemId,
    openQuickDuplicateModal,
    quoteItem,
    handleSendUsingWhatsAppWeb,
    isOrder,
    isQuote,
    confirmCreateDocument,
    onClickConfirmCreateDocument,
    onClickCloseConfirmCreateDocument,
    isOutgoingPayments,
    isReceipt,
    isMoveToReady,
    handleCheckboxChange,
    isDeliveryNote,
    isPurchaseDocument,
    isPurchaseOrder,
    purchaseOrderStatusOptions,
    updatePurchaseOrderStatus,
    isUpdatingPurchaseStatus,
    isOpenRelatedDocuments,
    setIsOpenRelatedDocuments,
    isPurchaseInvoice,
    systemVAT,
    isAddQuantity,
    openAddAttachmentsModal,
    onOpenAddAttachmentsModal,
    onCloseAddAttachmentsModal,
    onFileUploadStart,
    onFileUploadComplete,
    onClickOpenDeleteDeliveryModal,
    onClickCloseDeleteDeliveryModal,
    openDeleteDeliveryModal,
    onClickDeleteDelivery,
    contactsListRef,
    alertNotVerifiedMail,
    alertNotVerifiedModalState,
    AddCustomerModal,
    handleSaveDraftBtnClick,
    alertExistPurchaseNumber,
    pendingPurchaseNumber,
    setPendingPurchaseNumber,
    originalPurchaseNumber,
    setOriginalPurchaseNumber,
    updatePurchaseNumber,
    modalCallback,
    openReceiptModal,
    receiptSendModal,
    renderCreateNewButton,
    isNewCreation,
    blockInvoiceCreation,
    primaryColor,
    successColor,
    moveToReadyOption,
    setMoveToReadyOption,
    handleRadioChange,
    reCalculateItem,
    openAddAttachmentsPurchaseModal,
    onOpenAddAttachmentsPurchaseModal,
    onCloseAddAttachmentsPurchaseModal,
    openChangeStatusModal,
    onClickCloseChangeStatusModal,
    updateQuoteStatus,
    CheckPreferenceValue,
    openVatMismatchModal,
    onCloseVatMismatchModal,
    onConfirmVatMismatchModal,
    openTaxConfirmationWarningModal,
    onClickCloseTaxConfirmationWarningModal,
    estimatedProductionDate,
    systemDueDate,
    userDueDate,
    needExampleDate,
    handleExportToExcel,
    // FrontDesk Email Integration
    onClickSendEmailViaComposer,
    frontDeskSend,
    isEmailComposerOpen,
    emailData,
    isLoadingEmailData,
    isUpdatingDocumentPrices,
    closeEmailComposer: handleCloseEmailComposer,
    isMaterial,
    isMaterialPurchaseOrder,
    isCanIntake,
    openInventoryIntakeModal,
    onClickOpenInventoryIntakeModal,
    onClickCloseInventoryIntakeModal,
    inventoryData,
    inventoryLoading,
    openIntakeHistoryModal,
    onClickOpenIntakeHistoryModal,
    onClickCloseIntakeHistoryModal,
    intakeHistory,
    intakeHistoryLoading,
    openPurchaseOrderTypeModal,
    onOpenPurchaseOrderTypeModal,
    onClosePurchaseOrderTypeModal,
    handleSelectPurchaseOrderType,
    showIntakeValidationDialog,
    handleOpenIntakeFromValidation,
    handleCancelIntakeValidation,
    pendingStatusChange,
    handleUndoDocumentIntake,
    handleUndoItemIntake,
    handleShowRelatedDocuments,
    relatedDocumentsModalOpen,
    handleCloseRelatedDocumentsModal,
    relatedDocumentsData,
    relatedDocumentsLoading,
    showUndoIntakeDialog,
    handleConfirmUndoIntake,
    handleCancelUndoIntake,
    // Add item restriction dialog
    showAddItemRestrictionDialog,
    handleCreateNewPurchaseOrder,
    handleCancelAddItemRestriction,
    handleChangeStatusToUnderCreation,
    // Order update modal
    orderUpdateConfirmationModal,
    orderUpdateSendModal,
    handleOrderUpdateYes,
    handleOrderUpdateNo,
    handleOrderUpdateComplete,
    isOrderUpdateEmailComposer,
    getDocumentIdempotencyKey,
    openFinalPriceUpdateChoiceModal,
    settleFinalPriceUpdateChoice,
  };
};

export { useQuoteNew };
